import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";
import { fmtDate } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MonthJumpSheet } from "@/components/booking/MonthJumpSheet";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CheckoutDialog } from "@/components/booking/CheckoutDialog";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Info, ChevronsLeft, ChevronsRight, Plus, Trash2, Star, CreditCard, CalendarPlus, CalendarClock, Wallet, Phone, Pencil, User, UserPlus, MessageSquare, CheckCircle2, RotateCcw, Repeat, AlertTriangle, Check, ChevronsUpDown, X, Layers, Clock, Search, CalendarX, ShoppingBag, MoreVertical, ChevronDown, PanelLeft } from "lucide-react";
import { toast } from "sonner";
import { MedicalAlerts } from "@/components/booking/MedicalAlerts";
import { QuickSaleDialog } from "@/components/booking/QuickSaleDialog";
import { DepositDialog } from "@/components/booking/DepositDialog";
import { SourcePicker } from "@/components/booking/SourcePicker";
import { CalendarSidePanel } from "@/components/booking/CalendarSidePanel";
import { BookingIcons, DetailFlags, CalendarLegend, CalendarLegendDialog } from "@/components/booking/CalendarLegend";
import { PaymentPanel } from "@/components/booking/PaymentPanel";
import { QuickNotes } from "@/components/booking/QuickNotes";
import { CancelDialog } from "@/components/booking/CancelDialog";
import { PackagePrompt } from "@/components/booking/PackagePrompt";
import { BRAND } from "@/lib/palette";
import { ServiceLines, chainRows } from "@/components/booking/ServiceLines";
import { PulseCount } from "@/components/booking/PulseCount";
import { ClientCharts } from "@/components/booking/ClientCharts";
import { SnapshotChips } from "@/components/booking/ClientSnapshot";

let START_H = 8, END_H = 19;
const PX = (typeof window !== "undefined" && window.innerWidth < 768) ? 76 : 120;
const OH_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const computeHours = (oh) => {
  if (!oh) return [8, 19];
  let s = 24, e = 0;
  for (const d of OH_DAY_KEYS) {
    const day = oh[d];
    if (day && day.open) {
      const sh = parseInt(String(day.start || "09:00").slice(0, 2), 10);
      const ec = String(day.end || "17:00");
      let eh = parseInt(ec.slice(0, 2), 10);
      if (ec.slice(3, 5) !== "00") eh += 1;
      if (!isNaN(sh)) s = Math.min(s, sh);
      if (!isNaN(eh)) e = Math.max(e, eh);
    }
  }
  if (s >= e) return [8, 19];
  return [Math.max(0, Math.min(s, 23)), Math.min(24, Math.max(e, s + 1))];
};
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayStr = () => ymd(new Date());
const hm = (iso) => to12(iso.slice(11, 16));
const minsFromStart = (iso) => { const h = +iso.slice(11, 13), m = +iso.slice(14, 16); return (h - START_H) * 60 + m; };
const REBOOK_WEEKS = [2, 4, 6, 8, 12];
const tint = (hex, a) => {
  if (!hex || hex[0] !== "#") return hex;
  const n = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const r = parseInt(n.slice(1, 3), 16), g = parseInt(n.slice(3, 5), 16), b = parseInt(n.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const shade = (hex, f = 0.3) => {  // darker version of a hex colour (f = how much toward black)
  if (!hex || hex[0] !== "#") return hex;
  const n = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const c = (i) => Math.max(0, Math.round(parseInt(n.slice(i, i + 2), 16) * (1 - f)));
  return `rgb(${c(1)},${c(3)},${c(5)})`;
};

const lighten = (hex, f = 0.65) => {  // lighter version of a hex colour (f = how much toward white)
  if (!hex || hex[0] !== "#") return hex;
  const n = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const c = (i) => Math.min(255, Math.round(parseInt(n.slice(i, i + 2), 16) + (255 - parseInt(n.slice(i, i + 2), 16)) * f));
  return `rgb(${c(1)},${c(3)},${c(5)})`;
};

const to12 = (hm) => { const p = (hm || "0:0").split(":"); const H = Number(p[0]); const M = Number(p[1]); return `${(H % 12) || 12}:${pad(M)} ${H >= 12 ? "PM" : "AM"}`; };
const h12 = (H) => `${(H % 12) || 12} ${H >= 12 ? "PM" : "AM"}`;
const slotTime = (h, e) => { const r = e.currentTarget.getBoundingClientRect(); let m = Math.floor(((e.clientY - r.top) / PX) * 6) * 10; if (m >= 60) m = 50; if (m < 0) m = 0; return `${pad(h)}:${pad(m)}`; };
const TIMES = [];
for (let h = 6; h < 21; h++) for (let m = 0; m < 60; m += 5) TIMES.push(`${pad(h)}:${pad(m)}`);

const mondayOf = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // 0=Mon
  d.setDate(d.getDate() - day);
  return ymd(d);
};
const addDays = (dateStr, n) => { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() + n); return ymd(d); };
const dayLabel = (dateStr) => new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
// Compact header range label (Timely-style): "7 – 13 Sep, 2026" for week, full date for day.
const rangeText = (view, weekStart, date) => {
  if (view === "week") {
    const a = new Date(weekStart + "T00:00:00"), b = new Date(addDays(weekStart, 6) + "T00:00:00");
    return `${a.getDate()} – ${b.getDate()} ${b.toLocaleDateString(undefined, { month: "short" })}, ${b.getFullYear()}`;
  }
  return new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

// Side-by-side layout for overlapping bookings in a column: returns {id: {col, cols}}.
const collapseVisits = (items) => {
  // Merge same-visit (group_id) services into a single block: one client, listing all services.
  const groups = {};
  const out = [];
  for (const b of items) {
    if (b.group_id && !b.is_block) {
      // a visit is one client on ONE day — never merge group members that sit on different dates
      const key = `${b.group_id}|${(b.start || "").slice(0, 10)}`;
      (groups[key] = groups[key] || []).push(b);
    } else {
      out.push(b);
    }
  }
  for (const g of Object.values(groups)) {
    if (g.length === 1) { out.push(g[0]); continue; }
    const sorted = g.slice().sort((a, b) => (a.start < b.start ? -1 : 1));
    const first = sorted[0];
    const start = sorted.reduce((m, x) => (x.start < m ? x.start : m), first.start);
    const end = sorted.reduce((m, x) => (x.end > m ? x.end : m), first.end);
    out.push({
      ...first, start, end,
      services: sorted.map((x) => ({ id: x.id, service_name: x.service_name, start: x.start, service_id: x.service_id })),
      _memberIds: sorted.map((x) => x.id),
      _first: first,
      deposit_paid: sorted.some((x) => x.deposit_paid),
      paid: sorted.every((x) => x.paid),
      status: sorted.every((x) => x.status === "checked_out") ? "checked_out" : first.status,
    });
  }
  return out;
};

const computeLayout = (items) => {
  const evs = items.map((b) => ({ id: b.id, s: minsFromStart(b.start), e: minsFromStart(b.end) }))
    .sort((a, b) => a.s - b.s || a.e - b.e);
  const layout = {};
  let cluster = [], clusterEnd = null;
  const flush = () => {
    const colEnds = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < colEnds.length; i++) {
        if (colEnds[i] <= ev.s) { colEnds[i] = ev.e; layout[ev.id] = { col: i }; placed = true; break; }
      }
      if (!placed) { layout[ev.id] = { col: colEnds.length }; colEnds.push(ev.e); }
    });
    const total = colEnds.length || 1;
    cluster.forEach((ev) => { layout[ev.id].cols = total; });
    cluster = []; clusterEnd = null;
  };
  evs.forEach((ev) => {
    if (clusterEnd !== null && ev.s >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = clusterEnd === null ? ev.e : Math.max(clusterEnd, ev.e);
  });
  flush();
  return layout;
};

export default function CalendarPage() {
  const { user, business } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const cur = business?.currency || "AUD";
  const [quickSaleOpen, setQuickSaleOpen] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("cal_view") || "week");
  const [date, setDate] = useState(() => localStorage.getItem("cal_date") || todayStr());
  useEffect(() => { localStorage.setItem("cal_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("cal_date", date); }, [date]);
  const nowRef = useRef(null);
  const gridRef = useRef(null);
  const mobileDateRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", client_name: "", client_email: "", client_phone: "", client_dob: "", client_source: "", service_id: "", staff_id: "", time: "09:00", duration: "", price: "", notes: "", origin: "", campaign: "" });
  const [metaCampaigns, setMetaCampaigns] = useState(null);
  useEffect(() => {
    if (!["meta", "messenger"].includes(form.origin) || metaCampaigns !== null) return;
    api.get("/meta/ads/insights").then(({ data }) => setMetaCampaigns(data?.connected ? (data.campaigns || []).map((c) => c.campaign_name).filter(Boolean) : [])).catch(() => setMetaCampaigns([]));
  }, [form.origin, metaCampaigns]);
  const [enquiries, setEnquiries] = useState(null);
  useEffect(() => {
    if (form.origin !== "messenger" || enquiries !== null) return;
    api.get("/meta/enquiries").then(({ data }) => setEnquiries(data || [])).catch(() => setEnquiries([]));
  }, [form.origin, enquiries]);
  const ORIGINS = [["meta", "Facebook / Instagram ad"], ["messenger", "Messenger / Instagram DM"], ["google", "Google"], ["referral", "Friend / referral"], ["walk_in", "Walk-in / passing by"], ["repeat", "Existing client"], ["other", "Other"]];
  const attributionPayload = () => {
    if (!form.origin) return undefined;
    const src = { meta: "facebook", messenger: "facebook", google: "google", referral: "referral", walk_in: "walk-in", repeat: "repeat", other: "other" }[form.origin];
    return { utm_source: src, utm_medium: form.origin === "messenger" ? "messenger" : undefined, utm_campaign: ["meta", "messenger"].includes(form.origin) ? (form.campaign || "") : "" };
  };
  const [recur, setRecur] = useState({ on: false, frequency: "weekly", interval: 1, weekdays: [], endMode: "after", count: 6, end_date: "" });
  const [clientOpen, setClientOpen] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [weekStaffId, setWeekStaffId] = useState("");
  const [checkout, setCheckout] = useState(null); // booking being checked out
  const [tip, setTip] = useState("");
  const [products, setProducts] = useState([]);
  const [paying, setPaying] = useState(false);
  const [rebookFor, setRebookFor] = useState(null);
  const [tnotes, setTnotes] = useState([]);
  const [snap, setSnap] = useState(null);
  const [openChartId, setOpenChartId] = useState(null);
  const [visitCharts, setVisitCharts] = useState([]);
  const loadVisitCharts = (b) => (b?.client_id ? api.get(`/clients/${b.client_id}/charts`).then(({ data }) => setVisitCharts((data || []).filter((c) => c.booking_id === b.id))).catch(() => setVisitCharts([])) : setVisitCharts([]));
  useEffect(() => {
    setSnap(null); setVisitCharts([]);
    if (detail?.client_id && !detail.is_block) { api.get(`/clients/${detail.client_id}/snapshot`).then(({ data }) => setSnap(data)).catch(() => {}); loadVisitCharts(detail); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);
  const [tnText, setTnText] = useState("");
  const [tnSaving, setTnSaving] = useState(false);
  useEffect(() => {
    setTnotes([]); setTnText("");
    if (detail?.id && !detail.is_block) api.get(`/bookings/${detail.id}/treatment-notes`).then(({ data }) => setTnotes(data || [])).catch(() => {});
  }, [detail?.id]);
  const addTreatmentNote = async () => {
    const t = tnText.trim();
    if (!t) return;
    setTnSaving(true);
    try { const { data } = await api.post(`/bookings/${detail.id}/treatment-notes`, { text: t }); setTnotes((l) => [data, ...l]); setTnText(""); toast.success("Note added — locked with a timestamp"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save note"); }
    finally { setTnSaving(false); }
  };
  const [smsFor, setSmsFor] = useState(null);
  const [smsText, setSmsText] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const sendSms = async () => {
    const t = smsText.trim();
    if (!t || !smsFor?.client_id) return;
    setSmsSending(true);
    try {
      const { data } = await api.post(`/clients/${smsFor.client_id}/message`, { body: t });
      if (data?.status === "sent" || data?.status === "queued") toast.success("Text sent");
      else if (data?.status === "simulated") toast.info("Sent (simulated — live delivery pending Twilio approval)");
      else toast.warning(`Status: ${data?.status || "unknown"}`);
      setSmsFor(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send"); }
    finally { setSmsSending(false); }
  };
  const [pickingNew, setPickingNew] = useState(false); // browse diary to pick a slot for the new booking

  const pickNewSlot = (staffId, dayDate, time) => {
    if (waitlistEntry) {
      const w = waitlistEntry, sid = staffId || staff[0]?.id || "";
      const svcs = (w.services || []).length ? w.services : (w.service_id ? [{ id: w.service_id }] : [{ id: services[0]?.id }]);
      const rows = chainRows(svcs.map((s) => ({ service_id: s.id, staff_id: sid, time, duration: "", price: "" })), services, 1);
      setForm({ client_id: w.client_id || "", client_name: w.client_name || "", client_email: w.client_email || "", client_phone: w.client_phone || "", client_dob: "", client_source: "",
        service_id: rows[0].service_id, staff_id: sid, time, duration: "", price: "", notes: w.note ? `Waitlist: ${w.note}` : "", origin: "", campaign: "" });
      setExtraAppts(rows.slice(1));
      setNewClient(!w.client_id);
      setRecur({ on: false, frequency: "weekly", interval: 1, weekdays: [], endMode: "after", count: 6, end_date: "" });
      setMode("appointment");
    } else {
      setForm((f) => ({ ...f, staff_id: staffId || f.staff_id, time }));
    }
    if (dayDate) setDate(dayDate);
    setPickingNew(false);
    setOpen(true);
  };
  const [applyDeposit, setApplyDeposit] = useState(false);
  const [mode, setMode] = useState("appointment"); // appointment | block
  const [bf, setBf] = useState({ staff_id: "", date: "", time: "09:00", duration: 30, reason: "" });
  const setBfV = (k, v) => setBf((s) => ({ ...s, [k]: v }));
  const [extraAppts, setExtraAppts] = useState([]); // [{service_id, staff_id, time, duration, price}] for one client, in one go
  // One-line-per-service view: row 0 is the main booking (lives in `form`), the rest are extraAppts.
  const svcRows = useMemo(() => [{ service_id: form.service_id, staff_id: form.staff_id, time: form.time, duration: form.duration, price: form.price }, ...extraAppts], [form.service_id, form.staff_id, form.time, form.duration, form.price, extraAppts]);
  const setSvcRows = (rows) => {
    const [m, ...rest] = rows;
    setForm((f) => ({ ...f, service_id: m.service_id, staff_id: m.staff_id, time: m.time, duration: m.duration, price: m.price }));
    setExtraAppts(rest);
  };
  const [waitlistEntry, setWaitlistEntry] = useState(null); // booking someone off the waitlist
  const [editing, setEditing] = useState(false);
  const [dragBooking, setDragBooking] = useState(null);
  const [rescheduling, setRescheduling] = useState(null); // booking being rescheduled by picking a slot
  const [dropHint, setDropHint] = useState(null); // {key, top}
  const [searchOpen, setSearchOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [sendingDetails, setSendingDetails] = useState(false);
  const [sideOpen, setSideOpen] = useState(() => localStorage.getItem("cal_side") === "1");
  useEffect(() => { localStorage.setItem("cal_side", sideOpen ? "1" : "0"); }, [sideOpen]);
  const sideOpenBooking = (b) => { setView("day"); setDate(b.start.slice(0, 10)); if (window.innerWidth < 640) setSideOpen(false); setTimeout(() => setDetail(b), 60); };
  const sideBookFor = (c) => { openNew(undefined, undefined, undefined); setForm((f) => ({ ...f, client_id: c.id, client_name: c.name, client_phone: c.phone || "", client_email: c.email || "" })); if (window.innerWidth < 640) setSideOpen(false); };
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    if (!searchOpen) return;
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => { api.get(`/bookings/search?q=${encodeURIComponent(q)}`).then(({ data }) => setSearchResults(data.results || [])).catch(() => setSearchResults([])); }, 250);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen]);
  const [ef, setEf] = useState({ service_id: "", staff_id: "", date: "", time: "09:00", price: "", duration: "", notes: "" });
  const [editExtras, setEditExtras] = useState([]); // extra services to add to this appointment
  const setEfV = (k, v) => setEf((s) => ({ ...s, [k]: v }));
  // Changing the service should re-price and re-time the appointment to that service's defaults.
  const changeEfService = (v) => {
    const svc = services.find((s) => s.id === v);
    setEf((s) => ({ ...s, service_id: v, price: svc ? String(svc.price ?? "") : s.price, duration: svc?.duration_min ?? s.duration }));
  };
  const [quickNotes, setQuickNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => { setQuickNotes(detail?.notes || ""); /* eslint-disable-next-line */ }, [detail?.id]);
  useEffect(() => {
    if (detail?.client_id) {
      api.get(`/clients/${detail.client_id}/upcoming`).then(({ data }) => {
        // "Next appointment" = the client's next separate visit — not the other services inside this same grouped booking
        setUpcoming((data.upcoming || []).filter((b) => b.id !== detail.id && !(detail.group_id && b.group_id === detail.group_id) && Math.abs(new Date(b.start) - new Date(detail.start)) > 4 * 3600 * 1000));
        setClientStats({ visit_count: data.visit_count || 0, last_visit: data.last_visit || null });
      }).catch(() => { setUpcoming([]); setClientStats(null); });
    } else { setUpcoming([]); setClientStats(null); }
    /* eslint-disable-next-line */
  }, [detail?.id, detail?.client_id]);
  // Collapse same-visit services into ONE upcoming entry so the list isn't cluttered.
  const upcomingVisits = (() => {
    const groups = new Map(); const order = [];
    for (const u of upcoming) {
      const key = u.group_id || u.id;
      if (!groups.has(key)) { groups.set(key, []); order.push(key); }
      groups.get(key).push(u);
    }
    return order.map((key) => {
      const items = groups.get(key).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
      const first = items[0];
      return { key, first, items, start: first.start, service_id: first.service_id, color: first.color,
               names: items.map((i) => i.service_name), status: first.status };
    });
  })();
  const saveQuickNotes = async () => {
    if (!detail) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/bookings/${detail.id}/notes`, { notes: quickNotes });
      setDetail(data); load(); toast.success("Note saved to client file");
    } catch { toast.error("Couldn't save note"); }
    finally { setSavingNotes(false); }
  };
  const startEdit = (b) => {
    setEf({ service_id: b.service_id, staff_id: b.staff_id, date: b.start.slice(0, 10), time: b.start.slice(11, 16), price: b.price ?? "", duration: Math.round((new Date(b.end) - new Date(b.start)) / 60000), notes: b.notes || "", series_scope: "following" });
    setEditExtras([]);
    setRecur({ on: false, frequency: "weekly", interval: 1, weekdays: [], endMode: "after", count: 6, end_date: "" });
    setEditing(true);
  };
  const svcEnd = (svcId, timeStr) => {
    const svc = services.find((x) => x.id === svcId);
    const dur = svc?.duration_min || 30;
    const [h, m] = (timeStr || "09:00").split(":").map(Number);
    const t = h * 60 + m + dur;
    return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  };
  // Group services by category for grouped pickers.
  const svcGroups = useMemo(() => {
    const cn = Object.fromEntries(categories.map((c) => [c.id, c.name]));
    const g = {};
    services.forEach((s) => { const k = (s.category_id && cn[s.category_id]) || "Other services"; (g[k] = g[k] || []).push(s); });
    return Object.entries(g);
  }, [services, categories]);
  const svcOpts = (meta) => svcGroups.map(([cat, items]) => (
    <SelectGroup key={cat}>
      <SelectLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">{cat}</SelectLabel>
      {items.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{meta ? ` · ${s.duration_min}m · ${fmtCurrency(s.price, cur, 0)}` : ""}</SelectItem>)}
    </SelectGroup>
  ));
  // Other (linked) appointments the same client has on the edited day.
  const linkedFor = (d) => bookings.filter((b) => d && b.id !== d.id && !b.is_block && b.status !== "cancelled"
    && (b.start || "").slice(0, 10) === ef.date
    && ((d.client_id && b.client_id === d.client_id) || (!d.client_id && !!b.client_name && b.client_name === d.client_name)));
  const changeLinkedSvc = async (b, newId) => {
    try {
      await api.put(`/bookings/${b.id}`, { client_id: b.client_id || "", client_name: b.client_name, service_id: newId, staff_id: b.staff_id, start: b.start, price: null, notes: b.notes || "" });
      load(); toast.success("Service updated");
    } catch { toast.error("Couldn't update that service"); }
  };
  const changeLinkedTime = async (b, newTime) => {
    try {
      const start = `${(b.start || "").slice(0, 10)}T${newTime}:00+00:00`;
      await api.put(`/bookings/${b.id}`, { client_id: b.client_id || "", client_name: b.client_name, service_id: b.service_id, staff_id: b.staff_id, start, price: b.price ?? null, notes: b.notes || "" });
      load(); toast.success("Time updated");
    } catch { toast.error("Couldn't update that time"); }
  };
  const removeLinked = async (b) => {
    try { await api.delete(`/bookings/${b.id}`); load(); toast.success("Appointment removed"); }
    catch { toast.error("Couldn't remove that appointment"); }
  };
  const [cancelReq, setCancelReq] = useState(null); // { kind: 'remove'|'visit'|'status'|'skip', b, ... }
  const cancelVisit = (b) => {
    const n = bookings.filter((x) => x.group_id === b.group_id && x.status !== "cancelled" && x.status !== "checked_out").length;
    setCancelReq({ kind: "visit", b, title: "Cancel whole visit", description: `All ${n} services in this visit will be cancelled.`, confirmLabel: "Cancel visit" });
  };
  const runCancel = async ({ reason, note, notify }) => {
    const { kind, b } = cancelReq;
    const q = { reason, note, notify };
    try {
      if (kind === "remove") await api.delete(`/bookings/${b.id}`, { params: q });
      else if (kind === "visit") await api.post(`/bookings/${b.id}/cancel-visit`, q);
      else if (kind === "skip") { if (cancelReq.grouped) await api.post(`/bookings/${b.id}/cancel-visit`, q); else await api.patch(`/bookings/${b.id}/status`, { status: "cancelled", ...q }); }
      else await api.patch(`/bookings/${b.id}/status`, { status: "cancelled", ...q });
      setDetail(null); load();
      toast.success(kind === "skip" ? "This date skipped — series kept" : `${kind === "visit" ? "Visit" : "Booking"} cancelled${notify ? "" : " — no text sent"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel"); }
  };
  const saveEdit = async () => {
    try {
      const linked = linkedFor(detail);
      const hasExtras = editExtras.some((a) => a.service_id && a.time);
      // Ensure the whole visit shares one group so every service stays linked, moves together and
      // checks out on a single bill — this covers added services AND the client's other same-day services.
      const needGroup = hasExtras || linked.length > 0;
      const groupId = detail.group_id || (needGroup ? (crypto.randomUUID ? crypto.randomUUID() : `g-${Date.now()}-${Math.random()}`) : null);
      const { data } = await api.put(`/bookings/${detail.id}`, {
        client_id: detail.client_id || "", client_name: detail.client_name,
        service_id: ef.service_id, staff_id: ef.staff_id, start: `${ef.date}T${ef.time}:00+00:00`,
        price: ef.price === "" ? null : parseFloat(ef.price), notes: ef.notes, series_scope: ef.series_scope,
        duration_min: ef.duration === "" || ef.duration == null ? null : parseInt(ef.duration),
        group_id: groupId, group_move: !!detail.group_id,
      });
      // Pull the client's other same-day services into the same visit group so they bill together.
      for (const lb of linked) {
        if (groupId && lb.group_id !== groupId) {
          await api.put(`/bookings/${lb.id}`, {
            client_id: lb.client_id || "", client_name: lb.client_name,
            service_id: lb.service_id, staff_id: lb.staff_id, start: lb.start,
            price: lb.price ?? null, notes: lb.notes || "", group_id: groupId,
          }).catch(() => {});
        }
      }
      let added = 0;
      for (const a of editExtras) {
        if (!a.service_id || !a.time) continue;
        await api.post("/bookings", {
          client_id: detail.client_id || "", client_name: detail.client_name,
          service_id: a.service_id, staff_id: a.staff_id || ef.staff_id, start: `${ef.date}T${a.time}:00+00:00`, notes: ef.notes,
          duration_min: a.duration === "" || a.duration == null ? undefined : parseInt(a.duration),
          price: a.price === "" || a.price == null ? undefined : parseFloat(a.price),
          group_id: groupId, skip_confirmation: true,
        });
        added += 1;
      }
      let seriesMsg = "";
      if (recur.on && !detail.series_id) {
        try {
          const { data: r } = await api.post(`/bookings/${detail.id}/make-recurring`, {
            recurrence: { frequency: recur.frequency, interval: Number(recur.interval) || 1, weekdays: recur.frequency === "weekly" ? recur.weekdays : undefined, count: recur.endMode === "after" ? Number(recur.count) : undefined, end_date: recur.endMode === "on" ? recur.end_date : undefined },
          });
          seriesMsg = ` · recurring series of ${1 + Math.round((r.created || 0) / (r.per_occurrence || 1))}`;
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the recurring series"); }
      }
      setEditExtras([]); setDetail(data); setEditing(false); load();
      toast.success((added ? `Booking updated · ${added} service${added === 1 ? "" : "s"} added` : "Booking updated") + seriesMsg);
    } catch { toast.error("Couldn't update booking"); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const STATUSES = ["confirmed", "arrived", "checked_out", "cancelled", "no_show"];
  const label = (s) => (s || "confirmed").replace("_", " ");

  const weekStart = useMemo(() => mondayOf(date), [date]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const loadSeq = useRef(0);  // drop out-of-order responses (e.g. today's load landing after ?date=… load)
  const load = () => {
    const range = view === "week"
      ? { start: `${weekStart}T00:00:00+00:00`, end: `${addDays(weekStart, 6)}T23:59:59+00:00` }
      : { start: `${date}T00:00:00+00:00`, end: `${date}T23:59:59+00:00` };
    const seq = ++loadSeq.current;
    api.get("/bookings", { params: range })
      .then(({ data }) => { if (seq === loadSeq.current) setBookings(Array.isArray(data) ? data : []); })
      .catch((e) => { console.error("Failed to load bookings", e); toast.error(e?.response?.status ? `Couldn't load calendar (${e.response.status})` : "Couldn't load calendar — check your connection"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, view]);
  // Deep-link from the dashboard day sheet: ?date=…&open=<id> or ?checkout=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const d = sp.get("date"); if (d && d !== date) { setView("day"); setDate(d); }
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => {
    const openId = sp.get("open"), payId = sp.get("checkout");
    if (!openId && !payId) return;
    const b = bookings.find((x) => x.id === (openId || payId) || x.group_id === (openId || payId));
    if (b) {
      if (payId) openCheckout(b._first || b); else setDetail(b._first || b);
      const n = new URLSearchParams(sp); n.delete("open"); n.delete("checkout"); n.delete("date"); setSp(n, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [bookings]);
  // ?waitlist=<id>: book someone off the waitlist — tap a time, the sheet opens pre-filled.
  useEffect(() => {
    const wid = sp.get("waitlist");
    if (!wid || !services.length) return;
    api.get(`/waitlist/${wid}`).then(({ data }) => {
      setWaitlistEntry(data);
      if (data.preferred_date) { setView("day"); setDate(data.preferred_date); }
      setPickingNew(true);
      toast.info(`Tap a time on the diary for ${data.client_name}`);
    }).catch(() => { toast.error("Waitlist entry not found"); setSp({}, { replace: true }); });
    /* eslint-disable-next-line */
  }, [sp, services.length]);
  const setSearchParams = setSp;
  // scroll the diary so the current time sits ~1/3 down (reliable even if the now-line isn't rendered)
  const scrollToNow = () => {
    const g = gridRef.current;
    if (!g) return;
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const startMins = START_H * 60, endMins = END_H * 60;
    if (mins >= startMins && mins <= endMins) {
      const y = ((mins - startMins) / 60) * PX;
      g.scrollTo({ top: Math.max(0, y - g.clientHeight / 3), behavior: "smooth" });
    } else {
      g.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  // auto-open at the current time when looking at today (on load, date/view change, or after data loads)
  useEffect(() => {
    if (date === todayStr()) {
      const t = setTimeout(scrollToNow, 350);
      return () => clearTimeout(t);
    }
  }, [date, view, bookings.length]);
  // "Today" button: always jump to today AND scroll to now, even if already on today / scrolled away
  const goToday = () => {
    setDate(todayStr());
    setTimeout(scrollToNow, 400);
  };
  useEffect(() => {
    api.get("/staff").then(({ data }) => {
      const b = data.filter((s) => s.can_be_booked).sort((x, y) => (x.name || "").localeCompare(y.name || ""));
      setStaff(b);
      // Default the week column to the logged-in user when they're bookable, else the first staff — so
      // the calendar never lands on an empty column just because of staff list ordering.
      const mine = b.find((s) => s.id === user?.id);
      if (b[0]) setWeekStaffId((w) => w || (mine ? mine.id : b[0].id));
    }).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const [mobileStaffIdx, setMobileStaffIdx] = useState(0);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const allStaff = staff.length ? staff : [{ id: "_none", name: "Staff", color: BRAND.sageGrey }];
  const mobileDay = isMobile && view === "day";
  const safeMobileIdx = Math.min(mobileStaffIdx, allStaff.length - 1);
  const cols = mobileDay ? [allStaff[safeMobileIdx]] : allStaff;
  const shiftDate = (d) => setDate(addDays(date, view === "week" ? d * 7 : d));
  const [monthOpen, setMonthOpen] = useState(false);
  // Swipe left/right on the phone diary: day view → ±1 day; week view → ±1 week (only when the columns are already at the scroll edge).
  const touchStartRef = useRef(null);
  const [swipeDir, setSwipeDir] = useState(0);
  const onCalTouchStart = (e) => {
    if (e.touches.length !== 1) { touchStartRef.current = null; return; }
    const t = e.touches[0];
    const g = gridRef.current;
    touchStartRef.current = { x: t.clientX, y: t.clientY, at: Date.now(), sl: g?.scrollLeft ?? 0, max: g ? g.scrollWidth - g.clientWidth : 0 };
  };
  const onCalTouchEnd = (e) => {
    const s = touchStartRef.current; touchStartRef.current = null;
    if (!s || dragBooking) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5 || Date.now() - s.at > 900) return;
    const dir = dx < 0 ? 1 : -1;
    if (view === "week") {
      const g = gridRef.current;
      if (!g || Math.abs((g.scrollLeft ?? 0) - s.sl) > 4) return;
      if (dir === 1 && s.sl < s.max - 2) return;
      if (dir === -1 && s.sl > 2) return;
    }
    setSwipeDir(dir);
    shiftDate(dir);
  };
  useEffect(() => {
    if (!swipeDir) return;
    const id = setTimeout(() => setSwipeDir(0), 260);
    return () => clearTimeout(id);
  }, [swipeDir, date]);

  const openNew = (staffId, time, forDate) => {
    if (forDate) setDate(forDate);
    if (waitlistEntry) { setWaitlistEntry(null); setSp({}, { replace: true }); }
    setForm({ client_id: "", client_name: "", client_email: "", client_phone: "", client_dob: "", client_source: "", service_id: services[0]?.id || "", staff_id: staffId || staff[0]?.id || "", time: time || "09:00", duration: "", price: "", notes: "", origin: "", campaign: "" });
    setNewClient(false);
    setRecur({ on: false, frequency: "weekly", interval: 1, weekdays: [], endMode: "after", count: 6, end_date: "" });
    setMode("appointment"); setExtraAppts([]);
    setBf({ staff_id: staffId || staff[0]?.id || "", date: forDate || date, time: time || "09:00", duration: 30, reason: "" });
    setOpen(true);
  };

  const [deposit, setDeposit] = useState(null);
  const save = async () => {
    if (mode === "block") return createBlock();
    if (!form.service_id || !form.staff_id) { toast.error("Pick service & staff"); return; }
    const client = clients.find((c) => c.id === form.client_id);
    const clientName = client ? client.name : form.client_name || "Walk-in";
    const grouped = extraAppts.some((a) => a.service_id && a.time);
    const groupId = grouped ? (crypto.randomUUID ? crypto.randomUUID() : `g-${Date.now()}-${Math.random()}`) : null;
    const recPayload = recur.on ? { frequency: recur.frequency, interval: Number(recur.interval) || 1, weekdays: recur.frequency === "weekly" ? recur.weekdays : undefined, count: recur.endMode === "after" ? Number(recur.count) : undefined, end_date: recur.endMode === "on" ? recur.end_date : undefined } : undefined;
    try {
      const { data: created } = await api.post("/bookings", {
        client_id: form.client_id, client_name: clientName,
        client_email: form.client_email, client_phone: form.client_phone, client_dob: form.client_dob,
        referred_by_client_id: form.referred_by_client_id || "",
        client_source: form.client_source || ({ meta: "Meta ads (Facebook / Instagram)", messenger: "Meta ads (Messenger / DM)", google: "Google", referral: "Friend / word of mouth", walk_in: "Walk-in / passing by", repeat: "Existing client", other: "Other" }[form.origin] || ""),
        service_id: form.service_id, staff_id: form.staff_id, start: `${date}T${form.time}:00+00:00`, notes: form.notes,
        duration_min: form.duration === "" || form.duration == null ? undefined : parseInt(form.duration),
        price: form.price === "" || form.price == null ? undefined : parseFloat(form.price),
        group_id: groupId, skip_confirmation: grouped, attribution: attributionPayload(),
      });
      if (form.enquiry_id) await api.post(`/meta/enquiries/${form.enquiry_id}/link`, { booking_id: created.id }).catch(() => {});
      // Book several appointments for the same client in one go (one shared visit group).
      for (const a of extraAppts) {
        if (!a.service_id || !a.time) continue;
        await api.post("/bookings", {
          client_id: created.client_id || form.client_id, client_name: clientName,
          client_email: form.client_email, client_phone: form.client_phone,
          service_id: a.service_id, staff_id: a.staff_id || form.staff_id, start: `${date}T${a.time}:00+00:00`, notes: form.notes,
          duration_min: a.duration === "" || a.duration == null ? undefined : parseInt(a.duration),
          price: a.price === "" || a.price == null ? undefined : parseFloat(a.price),
          group_id: groupId, skip_confirmation: true,
        }).catch(() => {});
      }
      // One combined confirmation listing every service + time in the visit.
      if (grouped && groupId) await api.post("/bookings/group-confirmation", { group_id: groupId }).catch(() => {});
      if (waitlistEntry?.id) { await api.post(`/waitlist/${waitlistEntry.id}/booked`, { booking_id: created?.id || "" }).catch(() => {}); setWaitlistEntry(null); setSearchParams({}, { replace: true }); }
      // Recurring: build the series from the fully-priced anchor (after any manual price/discount
      // is applied above) so every future occurrence keeps the SAME manual price.
      let seriesOverlaps = 0;
      if (recur.on && created?.id) {
        try { const { data: ser } = await api.post(`/bookings/${created.id}/make-recurring`, { recurrence: recPayload }); seriesOverlaps = ser?.overlaps?.length || 0; }
        catch { toast.error("Booking created, but the recurring series couldn't be set up"); }
      }
      setOpen(false); load();
      if (recur.on && seriesOverlaps) {
        toast.warning(`Series booked — but ${seriesOverlaps} appointment${seriesOverlaps > 1 ? "s" : ""} overlap existing bookings. A to-do was added to everyone's list to reschedule them.`, { duration: 9000 });
      } else {
        toast.success(recur.on ? "Recurring series created" : extraAppts.length ? `${extraAppts.length + 1} bookings created` : "Booking created");
      }
      if (!recur.on && created?.id) setDeposit({ id: created.id, name: clientName });
    } catch { toast.error("Couldn't create booking"); }
  };
  const [eb, setEb] = useState(null);  // block edit form
  useEffect(() => {
    if (detail?.is_block) setEb({ staff_id: detail.staff_id, date: detail.start.slice(0, 10), time: detail.start.slice(11, 16), duration: Math.max(15, Math.round((new Date(detail.end) - new Date(detail.start)) / 60000)), reason: detail.service_name || "" });
    else setEb(null);
  }, [detail?.id, detail?.is_block]);
  const saveBlock = async () => {
    const [h, m] = eb.time.split(":").map(Number);
    const endM = h * 60 + m + Number(eb.duration);
    const endHM = `${pad(Math.floor(endM / 60))}:${pad(endM % 60)}`;
    try {
      await api.put(`/blocks/${detail.id}`, { staff_id: eb.staff_id, start: `${eb.date}T${eb.time}:00+00:00`, end: `${eb.date}T${endHM}:00+00:00`, reason: eb.reason || "Blocked" });
      setDetail(null); load(); toast.success("Blocked time updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't update"); }
  };
  const depositNote = (b) => (b.deposit_paid && b.deposit_amount ? ` · The ${fmtCurrency(b.deposit_amount, cur, 2)} deposit stays on the client's file as credit.` : "");
  const remove = (b) => setCancelReq({ kind: "remove", b, title: `Cancel ${b.service_name || "booking"}`, description: `${b.client_name || "Walk-in"} · ${fmtDate(b.start)} ${hm(b.start)}${depositNote(b)}`, confirmLabel: "Cancel booking" });
  const seriesAction = async (action, scope) => {
    const verb = action === "delete" ? "delete" : "cancel";
    const which = scope === "all" ? "the entire series" : "this and all later appointments";
    if (!window.confirm(`This will ${verb} ${which}. Continue?`)) return;
    try { const { data } = await api.post(`/bookings/${detail.id}/series-action`, { action, scope }); setDetail(null); load(); toast.success(`${data.affected} appointment${data.affected === 1 ? "" : "s"} ${data.action}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [seriesMove, setSeriesMove] = useState({ open: false, date: "", time: "09:00" });
  const moveSeries = async () => {
    if (!seriesMove.date) return toast.error("Pick a date");
    try { const { data } = await api.post(`/bookings/${detail.id}/series-reschedule`, { start: `${seriesMove.date}T${seriesMove.time}:00+00:00` }); setSeriesMove({ open: false, date: "", time: "09:00" }); setDetail(null); load(); toast.success(`Whole series moved (${data.affected})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  // Skip or move a SINGLE occurrence without touching the rest of the recurring series.
  const skipOccurrence = () => {
    const grouped = detail.group_id && bookings.filter((x) => x.group_id === detail.group_id && x.status !== "cancelled" && x.status !== "checked_out").length > 1;
    setCancelReq({ kind: "skip", b: detail, grouped, title: "Skip this date", description: "Only this occurrence is cancelled — the rest of the recurring series stays in place.", confirmLabel: "Skip this date" });
  };
  const [occMove, setOccMove] = useState({ open: false, date: "", time: "09:00" });
  const moveOccurrence = async () => {
    if (!occMove.date) return toast.error("Pick a date");
    try {
      await api.put(`/bookings/${detail.id}`, {
        client_id: detail.client_id || "", client_name: detail.client_name,
        service_id: detail.service_id, staff_id: detail.staff_id,
        start: `${occMove.date}T${occMove.time}:00+00:00`,
        price: detail.price ?? null, notes: detail.notes || "",
        series_scope: "this",
        duration_min: Math.round((new Date(detail.end) - new Date(detail.start)) / 60000) || null,
        group_id: detail.group_id || null, group_move: !!detail.group_id,
      });
      setOccMove({ open: false, date: "", time: "09:00" }); setDetail(null); load(); toast.success("This date moved — series kept");
    } catch { toast.error("Couldn't move this date"); }
  };
  const sendReminder = async (b) => {
    const grouped = b.group_id && bookings.filter((x) => x.group_id === b.group_id && x.status !== "cancelled" && x.status !== "checked_out").length > 1;
    try { const { data } = await api.post(`/bookings/${b.id}/${grouped ? "remind-visit" : "reminder"}`); toast.success(data.status === "simulated" ? "Reminder queued (simulated — add Twilio keys to send)" : (grouped ? "Visit reminder sent" : "Reminder sent")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send reminder"); }
  };
  const confirmVisit = async (b) => {
    try { await api.post(`/bookings/${b.id}/confirm-visit`); setDetail(null); load(); toast.success("Visit confirmed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm the visit"); }
  };
  const voidSale = async (b) => {
    const asCredit = window.confirm("Void this sale?\n\nOK = refund the amount paid to the client's account credit (and restock/return vouchers & credit used).\nCancel = reverse without a credit refund.");
    try {
      const { data } = await api.post(`/bookings/${b.id}/void`, { refund_as_credit: asCredit });
      setDetail(null); load();
      toast.success(asCredit && data.refunded_as_credit ? `Voided · ${fmtCurrency(data.refunded_as_credit, cur, 2)} added to client credit` : "Sale voided");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't void sale"); }
  };
  const createBlock = async () => {
    if (!bf.staff_id) { toast.error("Pick staff"); return; }
    const [h, m] = bf.time.split(":").map(Number);
    const endMin = h * 60 + m + (parseInt(bf.duration) || 30);
    const endHM = `${pad(Math.floor(endMin / 60))}:${pad(endMin % 60)}`;
    // build occurrence dates (recurring blocks handled client-side)
    const base = bf.date || date;
    const dates = [];
    if (recur.on) {
      const step = recur.frequency, iv = Number(recur.interval) || 1, n = Math.min(Number(recur.count) || 6, 60);
      let d = new Date(base + "T00:00:00");
      for (let i = 0; i < n; i++) {
        dates.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
        if (step === "daily") d.setDate(d.getDate() + iv);
        else if (step === "weekly") d.setDate(d.getDate() + 7 * iv);
        else d.setMonth(d.getMonth() + iv);
      }
    } else { dates.push(base); }
    try {
      for (const dt of dates) {
        await api.post("/blocks", { staff_id: bf.staff_id, start: `${dt}T${bf.time}:00+00:00`, end: `${dt}T${endHM}:00+00:00`, reason: bf.reason || "Blocked" });
      }
      setOpen(false); load();
      toast.success(dates.length > 1 ? `${dates.length} blocks created` : "Time blocked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't block time"); }
  };
  const changeStatus = async (b, status) => {
    if (status === "cancelled") {
      if (b.paid || b.status === "checked_out") { toast.error("Paid bookings went ahead and can't be cancelled — refund from the invoice instead."); return; }
      setCancelReq({ kind: "status", b, title: `Cancel ${b.service_name || "booking"}`, description: `${b.client_name || "Walk-in"} · ${fmtDate(b.start)} ${hm(b.start)}${depositNote(b)}`, confirmLabel: "Cancel booking" }); return;
    }
    try { await api.patch(`/bookings/${b.id}/status`, { status }); setDetail((d) => d && { ...d, status }); load(); toast.success(`Marked ${label(status)}`); }
    catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "medical_alerts") toast.error("Acknowledge the client's medical alerts above before starting treatment.");
      else toast.error((typeof det === "string" ? det : null) || "Failed");
    }
  };

  // ---- rebooking (Timely-style: jump the calendar to that week and pick the slot) ----
  const rebookNav = (b, weeks) => {
    const [d, t] = b.start.split("T");
    const target = addDays(d, weeks * 7);
    const time = (t || "09:00").slice(0, 5);
    const listPrice = services.find((s) => s.id === b.service_id)?.price;
    const keptPrice = b.price != null && Number(b.price) !== Number(listPrice) ? String(b.price) : "";
    setDetail(null); setRebookFor(null); setEditing(false);
    setView("day"); setDate(target);
    setForm({ client_id: b.client_id || "", client_name: b.client_name || "", client_email: "", client_phone: "", client_dob: "", client_source: "", service_id: b.service_id, staff_id: b.staff_id, time, duration: "", price: keptPrice, notes: "", origin: "", campaign: "" });
    setRecur({ on: false, frequency: "weekly", interval: 1, weekdays: [], endMode: "after", count: 6, end_date: "" });
    setMode("appointment"); setExtraAppts([]);
    setBf({ staff_id: b.staff_id, date: target, time, duration: 30, reason: "" });
    setOpen(true);
    if (keptPrice) toast.info(`Kept this client's price of $${keptPrice} from last time — change it if needed.`);
  };

  // ---- drag to reschedule ----
  const onDragOverCell = (e, key, hour) => {
    e.preventDefault();
    if (!dragBooking) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = Math.max(0, Math.min(PX - 1, e.clientY - rect.top));
    let mins = Math.floor((offsetY / PX) * 12) * 5; if (mins >= 60) mins = 55;
    setDropHint({ key, top: (hour - START_H) * PX + (mins / 60) * PX });
  };
  const onDropAt = async (e, staffId, dayDate, hour) => {
    e.preventDefault();
    setDropHint(null);
    const b = dragBooking; setDragBooking(null);
    if (!b) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = Math.max(0, Math.min(PX - 1, e.clientY - rect.top));
    let mins = Math.floor((offsetY / PX) * 12) * 5; if (mins >= 60) mins = 55;
    const newDate = dayDate || date;
    const newTime = `${pad(hour)}:${pad(mins)}`;
    const start = `${newDate}T${newTime}:00+00:00`;
    if (start.slice(0, 16) === b.start.slice(0, 16) && (staffId || b.staff_id) === b.staff_id) return;
    try {
      await api.put(`/bookings/${b.id}`, {
        client_id: b.client_id || "", client_name: b.client_name || "Walk-in", client_phone: b.client_phone || "",
        service_id: b.service_id, staff_id: staffId || b.staff_id, start, price: b.price ?? null, notes: b.notes || "", group_move: true,
        duration_min: Math.round((new Date(b.end) - new Date(b.start)) / 60000),
      });
      load(); toast.success(`Moved to ${dayLabel(newDate)} ${newTime}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't move booking"); }
  };

  // ---- reschedule by picking a slot on the calendar ----
  const beginReschedule = (b) => { setRescheduling(b); setDetail(null); setEditing(false); toast.info("Select a new day and time on the calendar"); };
  const doReschedule = async (staffId, dayDate, time) => {
    const b = rescheduling; if (!b) return;
    const newDate = dayDate || date;
    const start = `${newDate}T${time}:00+00:00`;
    if (start.slice(0, 16) === b.start.slice(0, 16) && (staffId || b.staff_id) === b.staff_id) { setRescheduling(null); return; }
    try {
      await api.put(`/bookings/${b.id}`, {
        client_id: b.client_id || "", client_name: b.client_name || "Walk-in", client_phone: b.client_phone || "",
        service_id: b.service_id, staff_id: staffId || b.staff_id, start, price: b.price ?? null, notes: b.notes || "", group_move: true,
        duration_min: Math.round((new Date(b.end) - new Date(b.start)) / 60000),
      });
      setRescheduling(null); load(); toast.success(`Moved to ${dayLabel(newDate)} ${time}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't move booking"); }
  };

  // ---- drag an appointment to a new time/staff (pointer events: works for mouse AND touch) ----
  const dragMoveTo = async (b, staffId, dateStr, hour, mins) => {
    const start = `${dateStr}T${pad(hour)}:${pad(mins)}:00+00:00`;
    if (start.slice(0, 16) === b.start.slice(0, 16) && (staffId || b.staff_id) === b.staff_id) return;
    try {
      await api.put(`/bookings/${b.id}`, {
        client_id: b.client_id || "", client_name: b.client_name || "Walk-in", client_phone: b.client_phone || "",
        service_id: b.service_id, staff_id: staffId || b.staff_id, start, price: b.price ?? null, notes: b.notes || "", group_move: true,
        duration_min: Math.round((new Date(b.end) - new Date(b.start)) / 60000),
      });
      load(); toast.success(`Moved to ${dayLabel(dateStr)} ${pad(hour)}:${pad(mins)}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't move booking"); }
  };

  const dragRef = useRef(null); // { b, moved, longTimer }
  const cellFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    return el && el.closest('[data-testid^="slot-"], [data-testid^="wslot-"]');
  };
  const updateDropHint = (x, y) => {
    const cell = cellFromPoint(x, y);
    if (!cell) { setDropHint(null); return; }
    const tid = cell.getAttribute("data-testid");
    const rect = cell.getBoundingClientRect();
    const offY = Math.max(0, Math.min(PX - 1, y - rect.top));
    let mins = Math.floor((offY / PX) * 12) * 5; if (mins >= 60) mins = 55;
    const isWeek = tid.startsWith("wslot-");
    const rest = tid.slice(isWeek ? 6 : 5); const li = rest.lastIndexOf("-");
    const key = isWeek ? rest.slice(0, li) : rest.slice(0, li);
    const hour = +rest.slice(li + 1);
    setDropHint({ key, top: (hour - START_H) * PX + (mins / 60) * PX });
  };
  const applyDrop = (b, x, y) => {
    const cell = cellFromPoint(x, y);
    if (!cell) return;
    const tid = cell.getAttribute("data-testid");
    const rect = cell.getBoundingClientRect();
    const offY = Math.max(0, Math.min(PX - 1, y - rect.top));
    let mins = Math.floor((offY / PX) * 12) * 5; if (mins >= 60) mins = 55;
    const b0 = b._first || b;
    if (tid.startsWith("wslot-")) {
      const rest = tid.slice(6); const li = rest.lastIndexOf("-");
      dragMoveTo(b0, weekStaffId, rest.slice(0, li), +rest.slice(li + 1), mins);
    } else {
      const rest = tid.slice(5); const li = rest.lastIndexOf("-");
      dragMoveTo(b0, rest.slice(0, li), date, +rest.slice(li + 1), mins);
    }
  };
  // Desktop drag-to-move (mouse). Touch is intentionally left to the browser so the diary always
  // scrolls smoothly; on touch a tap opens the booking (which has Reschedule).
  const justDragged = useRef(false);
  const onGlobalDragMove = (e) => {
    const d = dragRef.current; if (!d) return;
    if (!d.moved && (Math.abs(e.clientX - d.x0) > 5 || Math.abs(e.clientY - d.y0) > 5)) { d.moved = true; setDragBooking(d.b); }
    if (d.moved) { e.preventDefault(); updateDropHint(e.clientX, e.clientY); }
  };
  const endPointerDrag = (e) => {
    window.removeEventListener("pointermove", onGlobalDragMove);
    window.removeEventListener("pointerup", endPointerDrag);
    const d = dragRef.current; dragRef.current = null;
    setDragBooking(null); setDropHint(null);
    if (!d || !d.moved) return;
    justDragged.current = true; // suppress the click that follows a drag
    applyDrop(d.b, e.clientX, e.clientY);
  };
  const onBlockPointerDown = (b, e) => {
    if (e.pointerType === "touch") return; // let touch scroll / tap through
    if (b.is_block || b.status === "cancelled" || b.status === "checked_out") return;
    dragRef.current = { b, moved: false, x0: e.clientX, y0: e.clientY };
    window.addEventListener("pointermove", onGlobalDragMove, { passive: false });
    window.addEventListener("pointerup", endPointerDrag);
  };



  // ---- checkout ----
  const openCheckout = (b) => { setCheckout(b); setTip(""); setProducts([]); setDetail(null); setApplyDeposit(!!b.deposit_paid); };
  const productsTotal = products.reduce((a, p) => a + (parseFloat(p.amount) || 0), 0);
  const depositCredit = checkout && checkout.deposit_paid && applyDeposit ? (checkout.deposit_amount || 0) : 0;
  const checkoutTotal = checkout ? Math.max(0, (checkout.price || 0) + productsTotal + (parseFloat(tip) || 0) - depositCredit) : 0;
  const cleanProducts = () => products.filter((p) => p.name && parseFloat(p.amount) > 0).map((p) => ({ name: p.name, amount: parseFloat(p.amount) }));

  const payCard = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: checkout.id, tip: parseFloat(tip) || 0, products: cleanProducts(), origin_url: window.location.origin, apply_deposit: applyDeposit,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start payment"); setPaying(false); }
  };
  const payManual = async (method) => {
    setPaying(true);
    try {
      await api.post("/checkout/complete", { booking_id: checkout.id, tip: parseFloat(tip) || 0, products: cleanProducts(), method, apply_deposit: applyDeposit });
      toast.success("Sale recorded");
      const b = checkout; setCheckout(null); setPaying(false); load(); setRebookFor(b);
    } catch { toast.error("Couldn't record sale"); setPaying(false); }
  };

  const [ohStart, ohEnd] = useMemo(() => computeHours(business?.operating_hours), [business]);
  START_H = ohStart; END_H = ohEnd;
  const closedDatesSet = useMemo(() => new Set(business?.closed_dates || []), [business]);
  const isClosedDay = (dateStr) => {
    if (closedDatesSet.has(dateStr)) return true;
    const oh = business?.operating_hours;
    if (!oh) return false;
    const d = oh[OH_DAY_KEYS[(new Date(`${dateStr}T00:00:00`).getDay() + 6) % 7]];
    return d ? !d.open : false;
  };
  const closedLabel = (dateStr) => (closedDatesSet.has(dateStr) ? "Closed · Public holiday" : "Closed");
  const hours = useMemo(() => Array.from({ length: END_H - START_H }, (_, i) => START_H + i), [ohStart, ohEnd]);
  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base", numeric: true })),
    [clients]
  );
  // each booking is coloured by its service's category (falls back to the booking's stored colour)
  const svcCatColor = useMemo(() => {
    const catById = Object.fromEntries(categories.map((c) => [c.id, c.color]));
    const m = {};
    services.forEach((s) => { const cc = s.category_id ? catById[s.category_id] : null; if (cc) m[s.id] = cc; });
    return m;
  }, [services, categories]);

  const BookingBlock = ({ b, lay }) => {
    const containerH = (END_H - START_H) * PX;
    const rawTop = (minsFromStart(b.start) / 60) * PX;
    const dur = (minsFromStart(b.end) - minsFromStart(b.start));
    const h = Math.max((dur / 60) * PX - 2, PX / 6 - 2);
    const top = Math.max(0, Math.min(rawTop + 1, containerH - h - 1));
    const cols = lay?.cols || 1, col = lay?.col || 0;
    const widthPct = 100 / cols;
    const STATUS_BG = { checked_out: BRAND.forest, no_show: "#A94442" };
    const blockColor = (!b.is_block && svcCatColor[b.service_id]) || b.color || BRAND.taupe;
    // Soft pastel fill (category colour tinted toward white) + a full-strength colour bar on the left; dark text.
    const baseColor = STATUS_BG[b.status] || blockColor;
    const bgColor = b.is_block ? "rgba(140,136,128,0.16)" : lighten(baseColor, 0.78);
    const barColor = b.is_block ? BRAND.taupe : baseColor;
    const textColor = b.is_block ? "#1f2937" : shade(baseColor, 0.55);
    const onDark = false;
    const canDrag = !b.is_block && b.status !== "cancelled" && b.status !== "checked_out";
    // buffer tail (clean-down / turnaround time) shown as a striped strip at the foot of the block
    const bufMin = b.is_block ? 0 : (services.find((s) => s.id === b.service_id)?.buffer_min || 0);
    const bufH = bufMin > 0 ? Math.min(h - 8, (bufMin / 60) * PX) : 0;
    // Desktop mouse: drag to move. Touch: normal scroll + tap-to-open.
    const dragProps = canDrag ? { onPointerDown: (e) => onBlockPointerDown(b, e) } : {};
    const lifted = dragBooking?.id === b.id;
    return (
      <div data-testid={`booking-${b.id}`} onClick={(e) => { e.stopPropagation(); if (justDragged.current) { justDragged.current = false; return; } setDetail(b._first || b); }}
        {...dragProps}
        className={`absolute rounded-2xl pl-2.5 pr-1.5 py-1 text-xs overflow-hidden transition-[box-shadow,transform] duration-150 hover:shadow-lg active:scale-[0.985] animate-in fade-in-0 slide-in-from-top-1 ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${lifted ? "z-30 shadow-xl ring-2 ring-primary scale-[1.03]" : ""} ${dragBooking?.group_id && dragBooking.group_id === b.group_id && dragBooking.id !== b.id ? "ring-2 ring-primary ring-offset-1" : ""}`}
        style={{ top: top, height: h, left: `calc(${col * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`, backgroundColor: bgColor, color: textColor, borderLeft: `5px solid ${barColor}`, boxShadow: `0 1px 2px rgba(16,24,40,0.08), 0 3px 8px -2px rgba(16,24,40,0.16)`, opacity: b.status === "cancelled" ? 0.45 : 1, filter: b.status === "cancelled" ? "grayscale(0.6)" : "none" }}>
        {lifted && (
          <div className="absolute top-1 right-1 z-20 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow bg-primary" data-testid={`drag-hint-${b.id}`}>Moving…</div>
        )}
        {bufH > 6 && (
          <div className="absolute left-0 right-0 bottom-0 pointer-events-none" data-testid={`booking-buffer-${b.id}`}
            style={{ height: bufH, backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.25), rgba(255,255,255,0.25) 4px, transparent 4px, transparent 8px)", borderTop: "1px dashed rgba(255,255,255,0.6)" }} />
        )}
        <div className="flex items-start justify-between gap-1">
          <span className="font-semibold truncate leading-tight text-[13px]">
            {b.is_block ? (b.service_name || "Blocked") : (b.client_name || "Walk-in")}
          </span>
          <span className={`flex items-center gap-[3px] shrink-0 ${onDark ? "text-white/70" : "opacity-70"} ${isMobile ? "[&>*:not([data-keep])]:hidden" : ""}`}>
            {!b.is_block && <BookingIcons b={b} isMobile={isMobile} />}
            <button onClick={(e) => { e.stopPropagation(); remove(b); }} className={`opacity-60 hover:opacity-100 hover:text-red-300 shrink-0 ${b.services && b.services.length > 1 ? "hidden" : ""}`} data-testid={`booking-delete-${b.id}`}><Trash2 className="h-2.5 w-2.5" /></button>
          </span>
        </div>
        {b.services && b.services.length > 1 ? (
          <div className="space-y-0.5 mt-0.5">
            {b.services.map((sv) => (
              <div key={sv.id} className={`truncate leading-tight text-[11px] ${onDark ? "text-white/80" : "opacity-80"}`} data-testid={`visit-svc-${sv.id}`}>{sv.service_name} · {hm(sv.start)}</div>
            ))}
          </div>
        ) : (
          <>
            {!b.is_block && <div className="truncate opacity-80 leading-tight">{b.service_name} · {hm(b.start)}</div>}
            {b.is_block && <div className="truncate text-gray-500 leading-tight text-[10px]">{hm(b.start)}–{hm(b.end)}</div>}
          </>
        )}
        {!b.is_block && b.status === "arrived" && <div className="truncate uppercase text-[9px] font-bold text-emerald-200 tracking-wide">Arrived</div>}
      </div>
    );
  };

  // phones: ~2.5 day columns visible and swipeable, so names stay readable; desktop unchanged
  const gridStyle = (n) => ({ gridTemplateColumns: `48px repeat(${n}, minmax(${isMobile && view === "week" ? 136 : 104}px, 1fr))` });

  const NowLine = () => {
    const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
    useEffect(() => { const t = setInterval(() => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 60000); return () => clearInterval(t); }, []);
    const dayStart = START_H * 60, dayEnd = END_H * 60;
    if (nowMin < dayStart || nowMin > dayEnd) return null;
    const top = ((nowMin - dayStart) / 60) * PX;
    return (
      <div ref={nowRef} className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top }} data-testid="now-line">
        <span className="absolute -left-[3px] -top-[4px] h-2.5 w-2.5 rounded-full animate-pulse" style={{ backgroundColor: "#E06D53", boxShadow: "0 0 0 3px rgba(224,109,83,0.22)" }} />
        <div className="h-[2px] w-full" style={{ backgroundColor: "#E06D53" }} />
      </div>
    );
  };

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const breakFor = (staffId, dateStr) => {
    const s = staff.find((x) => x.id === staffId);
    if (!s || !dateStr) return null;
    const DK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const wh = s.working_hours?.[DK[(new Date(dateStr + "T00:00:00").getDay() + 6) % 7]];
    if (!wh || wh.open === false || !wh.break_start || !wh.break_end) return null;
    const hm = (v) => { const [h, m] = (v || "0:0").split(":"); return (+h) * 60 + (+m); };
    const a = hm(wh.break_start), b = hm(wh.break_end);
    return b > a ? { startMin: a, endMin: b } : null;
  };

  const workHoursFor = (staffId, dateStr) => {
    const s = staff.find((x) => x.id === staffId);
    if (!s || !dateStr) return null;
    const DK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const wh = s.working_hours?.[DK[(new Date(dateStr + "T00:00:00").getDay() + 6) % 7]];
    if (!wh) return null;
    const hm = (v) => { const [h, m] = (v || "0:0").split(":"); return (+h) * 60 + (+m); };
    if (wh.open === false) return { closed: true };
    return { closed: false, startMin: hm(wh.start || `${START_H}:00`), endMin: hm(wh.end || `${END_H}:00`) };
  };

  const OffHours = ({ info }) => {
    if (!info) return null;
    const stripe = { backgroundColor: "rgba(120,120,120,0.10)", backgroundImage: "repeating-linear-gradient(45deg, rgba(100,100,100,0.09), rgba(100,100,100,0.09) 5px, rgba(100,100,100,0.045) 5px, rgba(100,100,100,0.045) 10px)" };
    if (info.closed) return <div className="absolute inset-0 pointer-events-none z-0" style={stripe} data-testid="off-hours" />;
    const dayStart = START_H * 60, dayEnd = END_H * 60;
    return (<>
      {info.startMin > dayStart && <div className="absolute left-0 right-0 pointer-events-none z-0" style={{ top: 0, height: ((info.startMin - dayStart) / 60) * PX, ...stripe }} data-testid="off-hours" />}
      {info.endMin < dayEnd && <div className="absolute left-0 right-0 pointer-events-none z-0" style={{ top: ((info.endMin - dayStart) / 60) * PX, height: ((dayEnd - info.endMin) / 60) * PX, ...stripe }} data-testid="off-hours" />}
    </>);
  };

  const BreakBlock = ({ startMin, endMin }) => (
    <div className="absolute left-0 right-0 pointer-events-none flex items-start justify-center overflow-hidden" data-testid="break-block"
      style={{ top: (startMin / 60 - START_H) * PX, height: ((endMin - startMin) / 60) * PX,
        backgroundColor: "rgba(181,112,79,0.22)", zIndex: 1,
        backgroundImage: "repeating-linear-gradient(45deg, rgba(181,112,79,0.28), rgba(181,112,79,0.28) 8px, transparent 8px, transparent 16px)",
        borderTop: "2px solid rgba(181,112,79,0.7)", borderBottom: "2px solid rgba(181,112,79,0.7)" }}>
      <span className="text-[11px] font-bold uppercase tracking-widest mt-1.5 px-2 py-0.5 rounded-full bg-white/85" style={{ color: "#8A4F33" }}>☕ Break</span>
    </div>
  );

  const ClosedOverlay = ({ label }) => (
    <div className="absolute inset-0 z-0 pointer-events-none flex items-start justify-center pt-6"
      style={{ backgroundColor: "rgba(107,142,123,0.14)", backgroundImage: "repeating-linear-gradient(45deg, rgba(107,142,123,0.12), rgba(107,142,123,0.12) 6px, rgba(107,142,123,0.04) 6px, rgba(107,142,123,0.04) 12px)" }}
      data-testid="closed-overlay">
      <span className="inline-flex items-center gap-1 rounded-full bg-card/90 border border-border text-muted-foreground px-2.5 py-1 text-[11px] font-medium shadow-sm">
        <CalendarX className="h-3.5 w-3.5" />{label}
      </span>
    </div>
  );

  return (
    <div className={`max-w-full overflow-x-clip px-4 sm:px-6 py-2 sm:py-6 transition-[padding] ${sideOpen ? "sm:pl-[21.5rem]" : ""}`}>
      {sideOpen && <CalendarSidePanel clients={clients} staff={staff} cur={cur} onClose={() => setSideOpen(false)} onOpenBooking={sideOpenBooking} onBookFor={sideBookFor} onText={(c) => { setSmsFor({ client_id: c.id, client_name: c.name, client_phone: c.phone, start: new Date().toISOString() }); setSmsText(""); }} />}
      {/* mobile toolbar is sticky to the very top now that the redundant business-name header is hidden on calendar */}
      {dragBooking?.group_id && bookings.filter((x) => x.group_id === dragBooking.group_id).length > 1 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none" data-testid="group-drag-hint">
          <div className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-2 text-sm font-medium">
            <Layers className="h-4 w-4" />
            Moving the whole visit · {bookings.filter((x) => x.group_id === dragBooking.group_id).length} services move together
          </div>
        </div>
      )}
      <div className={`sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pr-4 sm:pr-6 xl:pr-20 bg-background transition-all duration-200 ${scrolled ? "py-2 mb-2 shadow-md border-b border-border" : "py-1.5 sm:py-3 mb-1.5 sm:mb-4"}`}>
        {/* Desktop / iPad toolbar — one row; labels collapse to icons below xl so iPad landscape doesn't wrap */}
        <div className="hidden md:flex flex-nowrap items-center justify-between gap-2" data-testid="cal-toolbar-desktop">
        <div className="flex items-center gap-2 flex-nowrap min-w-0">
          <Button variant="outline" size="icon" className="rounded-lg shrink-0" onClick={() => shiftDate(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="rounded-lg shrink-0" onClick={() => shiftDate(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-lg w-[9.25rem] xl:w-40 shrink-0" data-testid="cal-date" />
          <Button variant="ghost" className="rounded-lg" onClick={goToday} data-testid="cal-today">Today</Button>
          {view === "day" && isClosedDay(date) && (
            <span data-testid="cal-day-closed-badge" className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground border border-border px-2.5 py-1 text-xs font-medium">
              <CalendarX className="h-3.5 w-3.5" />{closedLabel(date)}
            </span>
          )}
          {!scrolled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-lg ml-1 px-2.5 xl:px-4 shrink-0" data-testid="week-jump-trigger" title="Jump forward / back by weeks">
                <CalendarClock className="h-4 w-4 xl:mr-1.5" /><span className="hidden xl:inline">Jump</span><ChevronsUpDown className="h-3.5 w-3.5 ml-1 xl:ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44" data-testid="week-jump-menu">
              <DropdownMenuLabel>Jump forward</DropdownMenuLabel>
              {REBOOK_WEEKS.map((w) => (
                <DropdownMenuItem key={`f${w}`} onClick={() => setDate(addDays(date, w * 7))} data-testid={`jump-fwd-${w}w`}>+{w} weeks</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Jump back</DropdownMenuLabel>
              {REBOOK_WEEKS.map((w) => (
                <DropdownMenuItem key={`b${w}`} onClick={() => setDate(addDays(date, -w * 7))} data-testid={`jump-back-${w}w`}>{"\u2212"}{w} weeks</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <div className="inline-flex rounded-lg border border-border overflow-hidden ml-1 shrink-0">
            <button onClick={() => setView("day")} data-testid="cal-view-day" className={`px-3 py-2 text-sm font-medium ${view === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>Day</button>
            <button onClick={() => setView("week")} data-testid="cal-view-week" className={`px-3 py-2 text-sm font-medium ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>Week</button>
          </div>
          {view === "week" && (
            <Select value={weekStaffId} onValueChange={setWeekStaffId}>
              <SelectTrigger className="h-10 rounded-lg w-32 xl:w-40 shrink-0" data-testid="cal-week-staff"><SelectValue placeholder="Staff" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-2 flex-nowrap justify-end shrink-0">
          <Button variant={sideOpen ? "default" : "outline"} size="icon" className="rounded-lg" onClick={() => setSideOpen((v) => !v)} data-testid="cal-side-toggle" title="Coming up & find client"><PanelLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="rounded-lg" onClick={() => { setSearchOpen(true); setSearchQ(""); setSearchResults([]); }} data-testid="cal-search-button" title="Search appointments"><Search className="h-4 w-4" /></Button>
          <CalendarLegend />
          <Button variant="outline" size="icon" className="rounded-lg xl:w-auto xl:px-4" onClick={() => setQuickSaleOpen(true)} data-testid="quick-sale-button" title="Quick sale"><ShoppingBag className="h-4 w-4 xl:mr-1.5" /><span className="hidden xl:inline">Sale</span></Button>
        </div>
        </div>
        {/* Mobile toolbar — one row: ‹ Today · date › ⋮ , with the D/W segment inline */}
        <div className="md:hidden space-y-1.5" data-testid="cal-toolbar-mobile">
          <div className="flex items-center gap-1">
            <button onClick={() => shiftDate(-1)} className="h-10 w-9 rounded-lg flex items-center justify-center text-muted-foreground active:scale-90 transition-transform shrink-0" data-testid="cal-prev-m"><ChevronLeft className="h-5 w-5" /></button>
            <Button variant="outline" size="sm" className="rounded-lg h-10 px-2 text-[11px] shrink-0" onClick={goToday} data-testid="cal-today-m">Today</Button>
            <button onClick={() => setMonthOpen(true)} className="flex-1 min-w-0 h-10 rounded-lg border border-border bg-card px-2 flex items-center justify-center gap-1 active:scale-[0.98] transition-transform" data-testid="cal-daterange-m">
              <span className="font-heading font-semibold text-[13px] truncate">{rangeText(view, weekStart, date).replace(/,? \d{4}$/, "").replace(", ", " ")}</span><ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
            <MonthJumpSheet open={monthOpen} onOpenChange={setMonthOpen} date={date} onPick={(d) => setDate(d)} />
            <CalendarLegendDialog open={legendOpen} onOpenChange={setLegendOpen} />
            <button onClick={() => shiftDate(1)} className="h-10 w-9 rounded-lg flex items-center justify-center text-muted-foreground active:scale-90 transition-transform shrink-0" data-testid="cal-next-m"><ChevronRight className="h-5 w-5" /></button>
            <div className="inline-flex rounded-lg border border-border overflow-hidden shrink-0 h-10">
              <button onClick={() => setView("day")} data-testid="cal-view-day-m" className={`px-2.5 text-xs font-semibold ${view === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>D</button>
              <button onClick={() => setView("week")} data-testid="cal-view-week-m" className={`px-2.5 text-xs font-semibold ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>W</button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="rounded-lg h-10 w-9 shrink-0" data-testid="cal-more-m"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => setSideOpen(true)} data-testid="cal-side-toggle-m"><PanelLeft className="h-4 w-4 mr-2" />Coming up & find client</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSearchOpen(true); setSearchQ(""); setSearchResults([]); }} data-testid="cal-search-button-m"><Search className="h-4 w-4 mr-2" />Search appointments</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setQuickSaleOpen(true)} data-testid="quick-sale-button-m"><ShoppingBag className="h-4 w-4 mr-2" />Quick sale</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMonthOpen(true)} data-testid="cal-jump-m"><CalendarClock className="h-4 w-4 mr-2" />Jump ahead 2–12 weeks…</DropdownMenuItem>
                <DropdownMenuItem onClick={() => shiftDate(view === "week" ? -28 : -7)}><ChevronsLeft className="h-4 w-4 mr-2" />Back a {view === "week" ? "month" : "week"}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => shiftDate(view === "week" ? 28 : 7)}><ChevronsRight className="h-4 w-4 mr-2" />Forward a {view === "week" ? "month" : "week"}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLegendOpen(true)} data-testid="cal-legend-button-m"><Info className="h-4 w-4 mr-2" />Icon key</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {view === "week" && (
            <Select value={weekStaffId} onValueChange={setWeekStaffId}>
              <SelectTrigger className="h-9 rounded-lg w-full" data-testid="cal-week-staff-m"><SelectValue placeholder="Staff" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col gap-0 max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-2xl">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Search className="h-4 w-4" />Find an appointment</DialogTitle></DialogHeader>
          <Input autoFocus placeholder="Search by client, phone number or service…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="h-11 rounded-lg mt-1" data-testid="cal-search-input" />
          <div className="mt-3 overflow-y-auto space-y-1.5" data-testid="cal-search-results">
            {searchQ.trim().length < 2 && <p className="text-sm text-muted-foreground py-6 text-center">Type a client or service name to search all appointments.</p>}
            {searchQ.trim().length >= 2 && searchResults.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No matching appointments.</p>}
            {searchResults.map((r) => (
              <button key={r.id} onClick={() => { setView("day"); setDate(r.start.slice(0, 10)); setSearchOpen(false); setTimeout(() => setDetail(r), 60); }}
                className="w-full flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-left hover:border-primary hover:bg-muted/40 transition-colors" data-testid={`cal-search-result-${r.id}`}>
                <span className="h-9 w-1 rounded-full shrink-0" style={{ backgroundColor: svcCatColor[r.service_id] || r.color || BRAND.sageGrey }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.client_name || "Walk-in"}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.service_name} · {fmtDate(r.start.slice(0, 10))} · {hm(r.start)}</div>
                </div>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-border capitalize text-muted-foreground shrink-0">{label(r.status)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {mobileDay && allStaff.length > 1 && allStaff[0].id !== "_none" && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-1 -mx-1 px-1 md:hidden" data-testid="mobile-staff-switcher">
          {allStaff.map((s, i) => (
            <button key={s.id} onClick={() => setMobileStaffIdx(i)} data-testid={`mobile-staff-${s.id}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${i === safeMobileIdx ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>
              <span className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: s.color }} />{s.name}
            </button>
          ))}
        </div>
      )}

      {rescheduling && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-800 text-white px-4 py-3 mb-1" data-testid="reschedule-banner">
          <div className="text-sm font-medium">
            Select a day and time for {rescheduling.client_name || "this booking"}
            <span className="hidden sm:inline text-slate-300 font-normal"> — {rescheduling.service_name}</span>
          </div>
          <button onClick={() => setRescheduling(null)} className="text-sm font-medium text-slate-200 hover:text-white" data-testid="reschedule-cancel">Cancel</button>
        </div>
      )}

      {pickingNew && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-primary text-primary-foreground px-4 py-3 mb-1" data-testid="picknew-banner">
          <div className="text-sm font-medium">
            Tap a time on the diary for {waitlistEntry ? `${waitlistEntry.client_name} (waitlist${waitlistEntry.service_name ? ` · ${waitlistEntry.service_name}` : ""})` : (form.client_name || "the new booking")}
          </div>
          {waitlistEntry
            ? <button onClick={() => { setPickingNew(false); setWaitlistEntry(null); setSp({}, { replace: true }); }} className="text-sm font-medium underline-offset-2 hover:underline" data-testid="picknew-cancel">Cancel</button>
            : <button onClick={() => { setPickingNew(false); setOpen(true); }} className="text-sm font-medium underline-offset-2 hover:underline" data-testid="picknew-cancel">Back to form</button>}
        </div>
      )}

      <Card className="border-0 rounded-none bg-card shadow-none overflow-hidden -mx-4 sm:-mx-6" data-testid="calendar-grid">
        <div ref={gridRef}
          className={`${mobileDay ? "overflow-y-auto overflow-x-hidden" : "overflow-auto"} max-h-[calc(100dvh-195px)] md:max-h-[calc(100vh-140px)]`}
          {...(isMobile ? { onTouchStart: onCalTouchStart, onTouchEnd: onCalTouchEnd } : {})}
          data-swipe-dir={swipeDir || undefined}
          style={{ WebkitOverflowScrolling: "touch", ...(mobileDay ? { touchAction: "pan-y" } : {}) }}>
        <div key={isMobile ? `${view}-${date}` : "grid"} className={`${mobileDay ? "min-w-0" : "min-w-[560px]"} ${swipeDir === 1 ? "animate-in slide-in-from-right-4 fade-in-0 duration-200" : swipeDir === -1 ? "animate-in slide-in-from-left-4 fade-in-0 duration-200" : ""}`}>
          {/* header — hidden on phones in day view: the staff chips above already name the selected therapist */}
          <div className={`grid border-b border-border sticky top-0 z-20 bg-card rounded-t-2xl shadow-[0_1px_0_0_rgba(0,0,0,0.04)] ${mobileDay ? "hidden" : ""}`} data-testid="diary-staff-header" style={gridStyle(view === "week" ? 7 : cols.length)}>
            <div className="p-2 sticky left-0 z-30 bg-card" />
            {view === "week"
              ? weekDates.map((d) => (
                  <div key={d} style={isClosedDay(d) ? { backgroundColor: "rgba(107,142,123,0.12)" } : undefined} className={`p-2 text-sm font-medium border-l border-border truncate ${d === todayStr() ? "text-primary" : ""} ${isClosedDay(d) ? "text-muted-foreground" : ""}`}>{dayLabel(d)}{isClosedDay(d) && <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">· Closed</span>}</div>
                ))
              : cols.map((s) => (
                  <div key={s.id} className="p-2 flex items-center gap-2 border-l border-border">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="font-medium text-sm truncate">{s.name}</span>
                  </div>
                ))}
          </div>
          {/* body */}
          <div className="grid" style={gridStyle(view === "week" ? 7 : cols.length)}>
            <div className="relative sticky left-0 z-10 bg-card" style={{ height: (END_H - START_H) * PX }}>
              {hours.map((h) => <div key={h} className="absolute left-0 right-0 text-[11px] text-muted-foreground px-1" style={{ top: (h - START_H) * PX - 6 }}>{h12(h)}</div>)}
            </div>
            {view === "week"
              ? weekDates.map((d) => (
                  <div key={d} className="relative border-l border-border" style={{ height: (END_H - START_H) * PX, backgroundColor: isClosedDay(d) ? "rgba(107,142,123,0.08)" : undefined }}>
                    {hours.map((h) => <div key={h} data-testid={`wslot-${d}-${h}`} className="absolute left-0 right-0 border-t border-border/50 hover:bg-muted/40 cursor-pointer" style={{ top: (h - START_H) * PX, height: PX }} onClick={(e) => rescheduling ? doReschedule(weekStaffId, d, slotTime(h, e)) : pickingNew ? pickNewSlot(weekStaffId, d, slotTime(h, e)) : openNew(weekStaffId, slotTime(h, e), d)} onDragOver={(e) => onDragOverCell(e, d, h)} onDrop={(e) => onDropAt(e, weekStaffId, d, h)}>{[10, 20, 30, 40, 50].map((mm) => <div key={mm} className="absolute left-0 right-0 border-t border-border/25 pointer-events-none" style={{ top: (mm / 60) * PX }} />)}</div>)}
                    {hours.map((h) => <div key={`l${h}`} className="absolute right-1 text-[10px] leading-none text-muted-foreground/60 tabular-nums pointer-events-none select-none" style={{ top: (h - START_H) * PX + 2 }}>{h12(h)}</div>)}
                    {dropHint?.key === d && <div className="absolute left-0 right-0 h-0.5 bg-primary z-10 pointer-events-none" style={{ top: dropHint.top }} data-testid="drop-hint" />}
                    <OffHours info={workHoursFor(weekStaffId, d)} />
                    {isClosedDay(d) && <ClosedOverlay label={closedLabel(d)} />}
                    {(() => { const l = breakFor(weekStaffId, d); return l ? <BreakBlock {...l} /> : null; })()}
                    {(() => { const items = collapseVisits(bookings.filter((b) => b.staff_id === weekStaffId && b.start.slice(0, 10) === d)); const lay = computeLayout(items); return items.map((b) => <BookingBlock key={b.id} b={b} lay={lay[b.id]} />); })()}
                    {d === todayStr() && <NowLine />}
                  </div>
                ))
              : cols.map((s) => (
                  <div key={s.id} className="relative border-l border-border" style={{ height: (END_H - START_H) * PX }}>
                    {hours.map((h) => <div key={h} data-testid={`slot-${s.id}-${h}`} className="absolute left-0 right-0 border-t border-border/50 hover:bg-muted/40 cursor-pointer" style={{ top: (h - START_H) * PX, height: PX }} onClick={(e) => rescheduling ? doReschedule(s.id, null, slotTime(h, e)) : pickingNew ? pickNewSlot(s.id, null, slotTime(h, e)) : openNew(s.id, slotTime(h, e))} onDragOver={(e) => onDragOverCell(e, s.id, h)} onDrop={(e) => onDropAt(e, s.id, null, h)}>{[10, 20, 30, 40, 50].map((mm) => <div key={mm} className="absolute left-0 right-0 border-t border-border/25 pointer-events-none" style={{ top: (mm / 60) * PX }} />)}</div>)}
                    {dropHint?.key === s.id && <div className="absolute left-0 right-0 h-0.5 bg-primary z-10 pointer-events-none" style={{ top: dropHint.top }} data-testid="drop-hint" />}
                    <OffHours info={workHoursFor(s.id, date)} />
                    {isClosedDay(date) && <ClosedOverlay label={closedLabel(date)} />}
                    {(() => { const l = breakFor(s.id, date); return l ? <BreakBlock {...l} /> : null; })()}
                    {(() => { const items = collapseVisits(bookings.filter((b) => b.staff_id === s.id && b.start.slice(0, 10) === date)); const lay = computeLayout(items); return items.map((b) => <BookingBlock key={b.id} b={b} lay={lay[b.id]} />); })()}
                    {date === todayStr() && <NowLine />}
                  </div>
                ))}
          </div>
        </div>
        </div>
      </Card>

      {/* Mobile quick-add booking */}
      {!rescheduling && (
        <button onClick={() => openNew()} data-testid="mobile-booking-fab" aria-label="New booking"
          className="md:hidden fixed bottom-20 left-5 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center active:scale-90 transition-transform hover:brightness-110">
          <CalendarPlus className="h-6 w-6" />
        </button>
      )}

      {/* New booking dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0 max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:border-x-0 max-sm:border-b-0">
          <DialogHeader className="shrink-0 pb-2"><DialogTitle className="font-heading">{waitlistEntry ? "Confirm waitlist booking" : "New booking"} · {fmtDate(date)}</DialogTitle><DialogDescription>Add an appointment, block out time, or book several at once.</DialogDescription></DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-3 space-y-4">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40 w-fit">
            <button type="button" onClick={() => setMode("appointment")} data-testid="mode-appointment" className={`px-3 py-1 text-xs rounded-md ${mode === "appointment" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}>Appointment</button>
            <button type="button" onClick={() => setMode("block")} data-testid="mode-block" className={`px-3 py-1 text-xs rounded-md ${mode === "block" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}>Block time</button>
          </div>
          {mode === "block" ? (
            <div className="space-y-3 py-1" data-testid="block-form">
              <Select value={bf.staff_id} onValueChange={(v) => setBfV("staff_id", v)}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="block-staff"><SelectValue placeholder="Staff" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-3 gap-3">
                <Input type="date" value={bf.date} onChange={(e) => setBfV("date", e.target.value)} className="h-11 rounded-lg" data-testid="block-date" />
                <Select value={bf.time} onValueChange={(v) => setBfV("time", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="block-time"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{to12(t)}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(bf.duration)} onValueChange={(v) => setBfV("duration", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="block-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>{[15, 30, 45, 60, 90, 120, 240, 480].map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input placeholder="Reason (e.g. Lunch, Training)" value={bf.reason} onChange={(e) => setBfV("reason", e.target.value)} className="h-11 rounded-lg" data-testid="block-reason" />
              <div className="rounded-lg border border-border p-3 space-y-2.5" data-testid="block-recurrence">
                <div className="flex items-center justify-between">
                  <div><span className="text-sm font-medium">Repeat this block</span><div className="text-xs text-muted-foreground">e.g. lunch every day / training weekly</div></div>
                  <Switch checked={recur.on} onCheckedChange={(v) => setRecur((r) => ({ ...r, on: v }))} data-testid="block-recur-toggle" />
                </div>
                {recur.on && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><label className="text-xs text-muted-foreground">Repeat</label>
                      <Select value={recur.frequency} onValueChange={(v) => setRecur((r) => ({ ...r, frequency: v }))}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="block-recur-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                      </Select></div>
                    <div className="space-y-1"><label className="text-xs text-muted-foreground">How many</label>
                      <Input type="number" min="2" max="60" value={recur.count} onChange={(e) => setRecur((r) => ({ ...r, endMode: "after", count: e.target.value }))} className="h-10 rounded-lg" data-testid="block-recur-count" /></div>
                  </div>
                )}
              </div>
            </div>
          ) : (
          <div className="space-y-3 py-1">
            <div className="space-y-1" data-testid="booking-day-field">
              <label className="text-xs font-medium text-muted-foreground">Day</label>
              <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-11 rounded-lg" data-testid="booking-date" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground block">Customer</label>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 rounded-lg text-xs gap-1"
                onClick={() => { if (newClient) { setNewClient(false); } else { set("client_id", ""); setNewClient(true); } }}
                data-testid="booking-toggle-newclient">
                {newClient ? (<><User className="h-3.5 w-3.5" />Search existing</>) : (<><UserPlus className="h-3.5 w-3.5" />New client</>)}
              </Button>
            </div>
            {!newClient && (
              <Popover open={clientOpen} onOpenChange={setClientOpen}>
                <PopoverTrigger asChild>
                  <button type="button" role="combobox" data-testid="booking-client"
                    className="h-11 w-full rounded-lg border border-input bg-background px-3 text-left text-sm flex items-center justify-between">
                    <span className={form.client_id ? "" : "text-muted-foreground"}>
                      {form.client_id ? (clients.find((c) => c.id === form.client_id)?.name || "Client") : "Search existing client…"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command>
                    <CommandInput placeholder="Type a name…" data-testid="booking-client-search" />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      {form.client_id && (
                        <CommandItem value="__clear clear selection" onSelect={() => { set("client_id", ""); setClientOpen(false); }} data-testid="booking-client-clear">
                          Clear selection
                        </CommandItem>
                      )}
                      <CommandItem value="__new add new client" onSelect={() => { set("client_id", ""); setNewClient(true); setClientOpen(false); }} data-testid="booking-client-addnew">
                        <UserPlus className="mr-2 h-4 w-4" />Add new client
                      </CommandItem>
                      {sortedClients.map((c) => (
                        <CommandItem key={c.id} value={`${c.name} ${c.phone || ""}`} onSelect={() => { set("client_id", c.id); setNewClient(false); setClientOpen(false); }} data-testid={`booking-client-opt-${c.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${form.client_id === c.id ? "opacity-100" : "opacity-0"}`} />{c.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            {newClient && <Input placeholder="New client name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="h-11 rounded-lg" data-testid="booking-client-name" autoFocus />}
            {newClient && (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} className="h-11 rounded-lg" data-testid="booking-client-email" />
                <Input placeholder="Phone" value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} className="h-11 rounded-lg" data-testid="booking-client-phone" />
                <Input type="date" placeholder="DOB" value={form.client_dob} onChange={(e) => set("client_dob", e.target.value)} className="h-11 rounded-lg col-span-2" data-testid="booking-client-dob" />
                <div className="col-span-2 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Referred from</label>
                  <SourcePicker value={form.client_source} onChange={(v) => set("client_source", v)} referredBy={form.referred_by_client_id} onReferredBy={(v) => set("referred_by_client_id", v)} clients={clients} testId="booking-client-source" />
                </div>
              </div>
            )}
            <div className="space-y-1.5" data-testid="booking-service-card">
              <label className="text-xs font-medium text-muted-foreground block">Services</label>
              <ServiceLines rows={svcRows} onChange={setSvcRows} services={services} svcGroups={svcGroups} staff={staff} times={TIMES} cur={cur} isAdmin={isAdmin}
                colorFor={(id) => svcCatColor[id] || BRAND.primary} testId="booking-svc" />
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="booking-origin-row">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Where did this booking come from?</label>
                <Select value={form.origin} onValueChange={(v) => set("origin", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="booking-origin"><SelectValue placeholder="Optional — helps ad tracking" /></SelectTrigger>
                  <SelectContent>{ORIGINS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.origin === "messenger" && enquiries && enquiries.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Link to the Messenger enquiry (sets the campaign automatically)</label>
                  <Select value={form.enquiry_id || ""} onValueChange={(v) => { const e = enquiries.find((x) => x.id === v); setForm((f) => ({ ...f, enquiry_id: v === "__none" ? "" : v, campaign: e?.campaign_name || f.campaign })); }}>
                    <SelectTrigger className="h-11 rounded-lg" data-testid="booking-enquiry"><SelectValue placeholder="Pick the chat this booking came from" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none">Not from a logged enquiry</SelectItem>{enquiries.map((e) => <SelectItem key={e.id} value={e.id}>{new Date(e.last_message_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })} · {e.campaign_name || e.ad_name || "ad " + e.ad_id}{e.preview ? ` · “${e.preview.slice(0, 40)}”` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {["meta", "messenger"].includes(form.origin) && !form.enquiry_id && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">{form.origin === "messenger" ? "Which ad started the chat?" : "Which ad / campaign?"}</label>
                  {metaCampaigns && metaCampaigns.length > 0 ? (
                    <Select value={form.campaign} onValueChange={(v) => set("campaign", v === "__unsure" ? "" : v)}>
                      <SelectTrigger className="h-11 rounded-lg" data-testid="booking-campaign"><SelectValue placeholder="Pick the campaign" /></SelectTrigger>
                      <SelectContent><SelectItem value="__unsure">Not sure which ad</SelectItem>{metaCampaigns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder={metaCampaigns === null ? "Loading campaigns…" : "Campaign / ad name (optional)"} value={form.campaign} onChange={(e) => set("campaign", e.target.value)} className="h-11 rounded-lg" data-testid="booking-campaign-text" />
                  )}
                </div>
              )}
            </div>
            <QuickNotes categoryId={(services.find((x) => x.id === form.service_id) || {}).category_id} clientId={form.client_id || undefined} context={{ clientName: form.client_name, serviceName: (services.find((x) => x.id === form.service_id) || {}).name, staffName: (staff.find((x) => x.id === form.staff_id) || {}).name }} onInsert={(n) => set("notes", (form.notes ? form.notes + "\n" : "") + n.resolved)} compact />
            <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="h-11 rounded-lg" data-testid="booking-notes" />
            <div className="rounded-lg border border-border p-3 space-y-2.5" data-testid="recurrence-block">
              <div className="flex items-center justify-between">
                <div><span className="text-sm font-medium">Repeat appointment</span><div className="text-xs text-muted-foreground">Create a recurring series</div></div>
                <Switch checked={recur.on} onCheckedChange={(v) => setRecur((r) => ({ ...r, on: v }))} data-testid="recur-toggle" />
              </div>
              {recur.on && (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><label className="text-xs text-muted-foreground">Repeat</label>
                      <Select value={recur.frequency} onValueChange={(v) => setRecur((r) => ({ ...r, frequency: v }))}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="recur-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><label className="text-xs text-muted-foreground">Repeat every</label>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="1" max="12" value={recur.interval} onChange={(e) => setRecur((r) => ({ ...r, interval: e.target.value }))} className="h-10 rounded-lg w-20" data-testid="recur-interval" />
                        <span className="text-sm text-muted-foreground">{recur.frequency === "daily" ? "day(s)" : recur.frequency === "monthly" ? "month(s)" : "week(s)"}</span>
                      </div>
                    </div>
                  </div>
                  {recur.frequency === "weekly" && (
                    <div className="space-y-1" data-testid="recur-weekdays">
                      <label className="text-xs text-muted-foreground">On which days</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {[["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]].map(([lbl, i]) => {
                          const on = (recur.weekdays || []).includes(i);
                          return (
                            <button key={i} type="button" data-testid={`recur-weekday-${i}`}
                              onClick={() => setRecur((r) => ({ ...r, weekdays: on ? r.weekdays.filter((x) => x !== i) : [...(r.weekdays || []), i] }))}
                              className={`h-9 w-11 rounded-lg text-sm border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-input hover:bg-muted"}`}>{lbl}</button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-muted-foreground">Leave empty to repeat on the same weekday as the start date.</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><label className="text-xs text-muted-foreground">Ends</label>
                      <Select value={recur.endMode} onValueChange={(v) => setRecur((r) => ({ ...r, endMode: v }))}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="recur-endmode"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="after">After N appointments</SelectItem><SelectItem value="on">On a date</SelectItem><SelectItem value="never">Never</SelectItem></SelectContent>
                      </Select>
                    </div>
                    {recur.endMode === "after" && <div className="space-y-1"><label className="text-xs text-muted-foreground">Appointments</label><Input type="number" min="2" max="60" value={recur.count} onChange={(e) => setRecur((r) => ({ ...r, count: e.target.value }))} className="h-10 rounded-lg" data-testid="recur-count" /></div>}
                    {recur.endMode === "on" && <div className="space-y-1"><label className="text-xs text-muted-foreground">Repeat until</label><Input type="date" value={recur.end_date} onChange={(e) => setRecur((r) => ({ ...r, end_date: e.target.value }))} className="h-10 rounded-lg" data-testid="recur-enddate" /></div>}
                  </div>
                  <div className="text-xs font-medium text-primary" data-testid="recur-total">Total: {recur.endMode === "after" ? `${Number(recur.count) || 0} appointments` : recur.endMode === "never" ? "up to 60 appointments" : recur.end_date ? `until ${recur.end_date}` : "—"}</div>
                  <p className="text-[11px] text-muted-foreground">Reschedule any appointment later and the rest of the series shifts to keep the same gap.</p>
                </div>
              )}
            </div>
          </div>
          )}
          </div>
          <DialogFooter className="shrink-0 border-t pt-4 gap-2"><Button variant="ghost" onClick={() => setOpen(false)} className="rounded-lg mr-auto" data-testid="booking-close"><X className="h-4 w-4 mr-1.5" />Close</Button>{mode === "appointment" && <Button variant="outline" onClick={() => { setOpen(false); setPickingNew(true); }} className="rounded-lg" data-testid="booking-pick-diary"><CalendarClock className="h-4 w-4 mr-1.5" />Find a time on the diary</Button>}<Button onClick={save} className="rounded-lg" data-testid="booking-save">{mode === "block" ? "Block time" : waitlistEntry ? "Confirm & save" : (extraAppts.length ? `Create ${extraAppts.length + 1} bookings` : "Create booking")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking detail dialog */}
      <Dialog open={!!smsFor} onOpenChange={(v) => !v && setSmsFor(null)}>
        <DialogContent className="max-w-md" data-testid="sms-dialog">
          <DialogHeader><DialogTitle className="font-heading">Text {smsFor?.client_name || "client"}</DialogTitle><DialogDescription>Sent from your clinic's sender name — one-way, replies aren't received.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            {[`Hi ${(smsFor?.client_name || "").split(" ")[0]}, just confirming your ${smsFor?.service_name || "appointment"} on ${smsFor ? new Date(smsFor.start.slice(0, 10) + "T12:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : ""} at ${smsFor ? to12(smsFor.start.slice(11, 16)) : ""}. See you then!`,
              `Hi ${(smsFor?.client_name || "").split(" ")[0]}, we're running a little behind today — thanks for your patience.`,
              `Hi ${(smsFor?.client_name || "").split(" ")[0]}, a spot has opened up earlier today if you'd like it — reply by calling us.`].map((t, i) => (
              <button key={i} type="button" onClick={() => setSmsText(t)} className="text-[11px] px-2 py-1 rounded-full border hover:border-primary hover:text-primary text-left" data-testid={`sms-preset-${i}`}>{["Confirm details", "Running late", "Earlier slot"][i]}</button>
            ))}
          </div>
          <Textarea value={smsText} onChange={(e) => setSmsText(e.target.value)} rows={4} placeholder="Type your message…" className="rounded-lg" data-testid="sms-text" />
          <div className="text-[11px] text-muted-foreground text-right tabular-nums">{smsText.length} chars · {Math.max(1, Math.ceil(smsText.length / 160))} SMS</div>
          <DialogFooter><Button onClick={sendSms} disabled={smsSending || !smsText.trim()} className="rounded-lg" data-testid="sms-send">{smsSending ? "Sending…" : "Send text"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail && !!detail.is_block && !!eb} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="block-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Blocked time</DialogTitle><DialogDescription>Change who, when and why — or remove the block.</DialogDescription></DialogHeader>
          {eb && (
            <div className="space-y-3">
              <Select value={eb.staff_id} onValueChange={(v) => setEb((x) => ({ ...x, staff_id: v }))}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="block-edit-staff"><SelectValue placeholder="Staff" /></SelectTrigger>
                <SelectContent>{staff.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-3 gap-3">
                <Input type="date" value={eb.date} onChange={(e) => setEb((x) => ({ ...x, date: e.target.value }))} className="h-11 rounded-lg" data-testid="block-edit-date" />
                <Select value={eb.time} onValueChange={(v) => setEb((x) => ({ ...x, time: v }))}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="block-edit-time"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{to12(t)}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(eb.duration)} onValueChange={(v) => setEb((x) => ({ ...x, duration: v }))}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="block-edit-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>{[15, 30, 45, 60, 90, 120, 240, 480].map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input placeholder="Reason (e.g. Training, Admin)" value={eb.reason} onChange={(e) => setEb((x) => ({ ...x, reason: e.target.value }))} className="h-11 rounded-lg" data-testid="block-edit-reason" />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg text-destructive mr-auto" onClick={() => remove(detail)} data-testid="block-edit-delete"><Trash2 className="h-4 w-4 mr-1.5" />Remove block</Button>
            <Button onClick={saveBlock} className="rounded-lg" data-testid="block-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail && !detail.is_block} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:border-x-0 max-sm:border-b-0">
          {detail && <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                {detail.client_id && (isAdmin || user?.role === "reception") ? (
                  <Link to={`/clients?client=${detail.client_id}`} className="hover:underline hover:text-primary flex items-center gap-1" data-testid="detail-client-link">
                    {detail.client_name}<User className="h-3.5 w-3.5 opacity-60" />
                  </Link>
                ) : detail.client_name}
              </DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2">
                <span>{detail.service_name} · {hm(detail.start)}–{hm(detail.end)} · {fmtCurrency(detail.price, cur, 2)}</span>
                {(() => { const c = clients.find((x) => x.id === detail.client_id); return c?.phone ? <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="detail-phone"><Phone className="h-3.5 w-3.5" />{c.phone}</a> : null; })()}
              </DialogDescription>
              <DetailFlags b={detail} />
            </DialogHeader>
            {!editing && detail.client_id && clientStats && (
              <div className="flex flex-wrap items-center gap-2 -mt-1" data-testid="client-mini-timeline">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" />{clientStats.visit_count} visit{clientStats.visit_count === 1 ? "" : "s"}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-muted text-muted-foreground px-2.5 py-1 text-xs font-medium">
                  <Clock className="h-3.5 w-3.5" />{clientStats.last_visit ? `Last visit ${fmtDate(clientStats.last_visit.slice(0, 10))}` : "First visit"}
                </span>
              </div>
            )}

            {editing ? (
              <div className="space-y-3 py-1" data-testid="detail-edit-form">
                <div className="rounded-lg border p-3 space-y-3" style={{ borderLeft: `5px solid ${svcCatColor[ef.service_id] || BRAND.primary}` }} data-testid="edit-service-card">
                <label className="text-xs font-medium text-muted-foreground block">Service</label>
                <Select value={ef.service_id} onValueChange={changeEfService}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="edit-service"><SelectValue placeholder="Service" /></SelectTrigger>
                  <SelectContent>{svcOpts(true)}</SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">With staff</label>
                  <Select value={ef.staff_id} onValueChange={(v) => setEfV("staff_id", v)}>
                    <SelectTrigger className="h-11 rounded-lg" data-testid="edit-staff"><SelectValue placeholder="Staff" /></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Day</label>
                  <Input type="date" value={ef.date} onChange={(e) => setEfV("date", e.target.value)} className="h-11 rounded-lg" data-testid="edit-date" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Starting time</label>
                  <Select value={ef.time} onValueChange={(v) => setEfV("time", v)}>
                    <SelectTrigger className="h-11 rounded-lg" data-testid="edit-time"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{to12(t)}</SelectItem>)}</SelectContent>
                  </Select></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Duration (min)</label>
                  <Input type="number" min="0" step="5" value={ef.duration} onChange={(e) => setEfV("duration", e.target.value)} className="h-11 rounded-lg tabular-nums" placeholder="e.g. 60" data-testid="edit-duration" /></div>
                </div>
                {isAdmin && (
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Price</label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{CURRENCIES[cur]?.symbol || "$"}</span>
                      <Input type="number" min="0" value={ef.price} onChange={(e) => setEfV("price", e.target.value)} className="h-11 rounded-lg pl-7 tabular-nums" placeholder="price" data-testid="edit-price" /></div>
                  </div>
                )}
                </div>
                <textarea placeholder="Booking notes" value={ef.notes} onChange={(e) => setEfV("notes", e.target.value)} className="w-full rounded-lg border border-input bg-background p-3 text-sm min-h-[70px]" data-testid="edit-notes" />
                {linkedFor(detail).length > 0 && (
                  <div className="rounded-lg border border-border p-3 space-y-2" data-testid="edit-linked-services">
                    <span className="text-xs font-medium text-muted-foreground">This client's other services this day — change or remove</span>
                    {linkedFor(detail).map((b) => (
                      <div key={b.id} className="flex items-center gap-2" data-testid={`edit-linked-row-${b.id}`}>
                        <Select value={b.service_id} onValueChange={(v) => changeLinkedSvc(b, v)}>
                          <SelectTrigger className="h-9 rounded-lg flex-1" data-testid={`edit-linked-service-${b.id}`}><SelectValue placeholder="Service" /></SelectTrigger>
                          <SelectContent>{svcOpts(true)}</SelectContent>
                        </Select>
                        <Select value={(b.start || "").slice(11, 16)} onValueChange={(v) => changeLinkedTime(b, v)}>
                          <SelectTrigger className="h-9 rounded-lg w-24 shrink-0" data-testid={`edit-linked-time-${b.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{to12(t)}</SelectItem>)}</SelectContent>
                        </Select>
                        <button type="button" onClick={() => removeLinked(b)} className="text-muted-foreground hover:text-red-500 p-1" data-testid={`edit-linked-remove-${b.id}`}><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded-lg border border-border p-3 space-y-2" data-testid="edit-extra-services">
                  <span className="text-xs font-medium text-muted-foreground">Add more services (same client, back-to-back)</span>
                  <ServiceLines rows={editExtras} onChange={setEditExtras} services={services} svcGroups={svcGroups} staff={staff} times={TIMES} cur={cur} isAdmin={isAdmin}
                    colorFor={(id) => svcCatColor[id] || BRAND.primary} testId="edit-extra" minRows={0}
                    anchor={{ service_id: ef.service_id, staff_id: ef.staff_id, time: ef.time, duration: ef.duration, price: ef.price }} />
                  <p className="text-[11px] text-muted-foreground">Totals include this appointment. To change or remove an existing service, use the list above.</p>
                </div>
                {!detail.series_id && (
                  <div className="rounded-lg border border-border p-3 space-y-2.5" data-testid="edit-recurrence-block">
                    <div className="flex items-center justify-between">
                      <div><span className="text-sm font-medium">Repeat appointment</span><div className="text-xs text-muted-foreground">Turn this into a recurring series</div></div>
                      <Switch checked={recur.on} onCheckedChange={(v) => setRecur((r) => ({ ...r, on: v }))} data-testid="edit-recur-toggle" />
                    </div>
                    {recur.on && (
                      <div className="space-y-2.5">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1"><label className="text-xs text-muted-foreground">Repeat</label>
                            <Select value={recur.frequency} onValueChange={(v) => setRecur((r) => ({ ...r, frequency: v }))}>
                              <SelectTrigger className="h-10 rounded-lg" data-testid="edit-recur-frequency"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                            </Select></div>
                          <div className="space-y-1"><label className="text-xs text-muted-foreground">Every</label>
                            <Input type="number" min="1" max="12" value={recur.interval} onChange={(e) => setRecur((r) => ({ ...r, interval: e.target.value }))} className="h-10 rounded-lg" data-testid="edit-recur-interval" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1"><label className="text-xs text-muted-foreground">Ends</label>
                            <Select value={recur.endMode} onValueChange={(v) => setRecur((r) => ({ ...r, endMode: v }))}>
                              <SelectTrigger className="h-10 rounded-lg" data-testid="edit-recur-endmode"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="after">After N</SelectItem><SelectItem value="on">On a date</SelectItem><SelectItem value="never">Never</SelectItem></SelectContent>
                            </Select></div>
                          {recur.endMode === "after" && <div className="space-y-1"><label className="text-xs text-muted-foreground">Appointments</label><Input type="number" min="2" max="60" value={recur.count} onChange={(e) => setRecur((r) => ({ ...r, count: e.target.value }))} className="h-10 rounded-lg" data-testid="edit-recur-count" /></div>}
                          {recur.endMode === "on" && <div className="space-y-1"><label className="text-xs text-muted-foreground">Until</label><Input type="date" value={recur.end_date} onChange={(e) => setRecur((r) => ({ ...r, end_date: e.target.value }))} className="h-10 rounded-lg" data-testid="edit-recur-enddate" /></div>}
                        </div>
                        <p className="text-[11px] text-muted-foreground">Repeats from this appointment's date; weekly uses the same weekday. Saving creates the future appointments.</p>
                      </div>
                    )}
                  </div>
                )}
                {detail.series_id && (
                  <div className="rounded-lg border border-border p-3 space-y-2" data-testid="edit-series-scope">
                    <div className="text-xs font-medium text-muted-foreground">This is a recurring appointment. Apply time change to:</div>
                    <div className="flex gap-2">
                      {[["this", "This only"], ["following", "This & following"]].map(([v, lbl]) => (
                        <button key={v} type="button" onClick={() => setEfV("series_scope", v)} data-testid={`edit-scope-${v}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${ef.series_scope === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={saveEdit} className="rounded-lg flex-1" data-testid="edit-save">Save changes</Button>
                  <Button variant="outline" onClick={() => setEditing(false)} className="rounded-lg" data-testid="edit-cancel">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-1 text-sm">
                {detail.client_id && <MedicalAlerts clientId={detail.client_id} bookingId={detail.id} compact />}
                {(() => {
                  const visit = detail.group_id ? bookings.filter((x) => x.group_id === detail.group_id && x.status !== "cancelled" && (x.start || "").slice(0, 10) === (detail.start || "").slice(0, 10)).sort((a, b) => (a.start < b.start ? -1 : 1)) : [];
                  if (visit.length < 2) return null;
                  const total = visit.reduce((s, x) => s + (x.price || 0), 0);
                  return (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="visit-services">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />This visit · {visit.length} services</span>
                        <span className="text-xs font-semibold tabular-nums">{fmtCurrency(total, cur, 2)}</span>
                      </div>
                      {visit.map((s) => (
                        <div key={s.id} className={`flex justify-between text-xs py-0.5 ${s.id === detail.id ? "font-semibold" : "text-muted-foreground"}`} data-testid={`visit-svc-${s.id}`}>
                          <span>{hm(s.start)} · {s.service_name}</span>
                          <span className="tabular-nums">{fmtCurrency(s.price || 0, cur, 2)}</span>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2.5">
                        <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => confirmVisit(detail)} data-testid="visit-confirm"><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Confirm visit</Button>
                        <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => sendReminder(detail)} data-testid="visit-remind"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Remind visit</Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">Checkout, remind and cancel apply to the whole visit. Use "Reschedule / edit" to change a single service.</p>
                    </div>
                  );
                })()}
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Status</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => changeStatus(detail, s)} data-testid={`status-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border ${detail.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{label(s)}</button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!detail.paid && detail.status !== "checked_out" && <Button onClick={() => openCheckout(detail)} className="rounded-lg flex-1 min-w-[140px]" data-testid="detail-checkout"><CreditCard className="h-4 w-4 mr-1.5" />Checkout</Button>}
                  {detail.client_id && <Button variant="outline" onClick={() => { setSmsFor(detail); setSmsText(""); }} className="rounded-lg" data-testid="detail-text-client"><MessageSquare className="h-4 w-4 mr-1.5" />Text</Button>}
                  <Button variant="outline" onClick={() => beginReschedule(detail)} className="rounded-lg flex-1 min-w-[120px]" data-testid="detail-reschedule"><CalendarClock className="h-4 w-4 mr-1.5" />Reschedule</Button>
                  <Button variant="outline" onClick={() => startEdit(detail)} className="rounded-lg flex-1 min-w-[100px]" data-testid="detail-edit"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
                  <Button variant="outline" onClick={() => sendReminder(detail)} className="rounded-lg flex-1 min-w-[110px]" data-testid="detail-reminder"><MessageSquare className="h-4 w-4 mr-1.5" />Remind</Button>
                </div>
                {(detail.paid || detail.status === "checked_out") && <PaymentPanel booking={detail} cur={cur} isAdmin={isAdmin} onVoid={() => voidSale(detail)} onChanged={load} />}
                {!detail.is_block && detail.client_id && <PackagePrompt booking={detail} onChanged={(patch) => { setDetail((d) => d && { ...d, ...patch }); load(); }} />}
                {!detail.paid && detail.deposit_pay_later && !detail.deposit_paid && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="detail-deposit-unpaid">
                    <span className="text-amber-900"><span className="font-semibold">Deposit unpaid</span> · {fmtCurrency(detail.deposit_required || detail.deposit_amount || 0, cur, 2)} (client chose to pay later{detail.deposit_reminder_sent ? ", reminder sent" : ""})</span>
                    {detail.deposit_pay_url && <Button size="sm" variant="outline" className="rounded-lg shrink-0" onClick={() => { navigator.clipboard?.writeText(detail.deposit_pay_url); toast.success("Payment link copied"); }} data-testid="detail-deposit-copy-link">Copy pay link</Button>}
                  </div>
                )}
                {!detail.is_block && !["checked_out", "cancelled", "no_show"].includes(detail.status) && new Date(detail.start) > new Date() && (
                  <Button variant="outline" className="rounded-lg w-full" disabled={sendingDetails} onClick={async () => { setSendingDetails(true); try { await api.post(`/bookings/${detail.id}/send-details`); toast.success("Appointment details texted"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); } finally { setSendingDetails(false); } }} data-testid="detail-text-details"><MessageSquare className="h-4 w-4 mr-1.5" />{sendingDetails ? "Sending…" : "Text details"}</Button>
                )}
                {!detail.is_block && !detail.paid && detail.status !== "checked_out" && detail.status !== "cancelled" && !detail.deposit_paid && (
                  <Button variant="outline" className="rounded-lg w-full" onClick={() => setDeposit({ id: detail.id, name: detail.client_name })} data-testid="detail-take-deposit"><Wallet className="h-4 w-4 mr-1.5" />Take deposit</Button>
                )}
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Rebook this client</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {REBOOK_WEEKS.map((w) => (
                      <button key={w} onClick={() => rebookNav(detail, w)} data-testid={`detail-rebook-${w}`}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border border-border text-muted-foreground hover:text-foreground hover:border-primary">+{w} wks</button>
                    ))}
                  </div>
                </div>
                {upcomingVisits.length > 0 && (
                  <div data-testid="detail-upcoming">
                    <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Next appointment</label>
                    <div className="mt-1.5 space-y-1.5">
                      {upcomingVisits.slice(0, 1).map((v) => (
                        <button key={v.key} onClick={() => { setView("day"); setDate(v.start.slice(0, 10)); setDetail(v.first); }}
                          className="w-full flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-left hover:border-primary hover:bg-muted/40 transition-colors active:scale-[0.99]" data-testid={`detail-upcoming-${v.key}`}>
                          <span className="h-8 w-1 rounded-full shrink-0" style={{ backgroundColor: svcCatColor[v.service_id] || v.color || BRAND.sageGrey }} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{v.items.length > 1 ? `${v.names.join(" + ")}` : v.names[0]}</div>
                            <div className="text-xs text-muted-foreground">{fmtDate(v.start.slice(0, 10))} · {hm(v.start)}{v.items.length > 1 ? ` · ${v.items.length} services` : ""}</div>
                          </div>
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-border capitalize text-muted-foreground shrink-0">{label(v.status)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div data-testid="detail-treatment-notes">
                  {detail.status === "checked_out" && (
                    <div className="mb-3" data-testid="detail-pulses">
                      <PulseCount bookingId={detail.id} booking={detail} onSaved={(d) => { setDetail({ ...detail, pulses: d.pulses, pulse_device_id: d.device_id, pulse_device_name: d.device_name, pulse_cost: d.pulse_cost }); load(); }} />
                    </div>
                  )}
                  {snap && Object.entries(snap).some(([k, val]) => k !== "migrated_from_legacy" && String(val || "").trim()) && <div className="mb-3" data-testid="detail-snapshot"><SnapshotChips snapshot={snap} compact /></div>}
                  <div className="flex items-center justify-between gap-2">
                    <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Visit notes</label>
                    {detail.client_id && (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid="detail-charts">
                        {visitCharts.map((c) => <button key={c.id} type="button" onClick={() => setOpenChartId(c.id)} className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-xs text-primary hover:bg-primary/10" data-testid={`detail-chart-${c.id}`}>{c.title || "Chart"}</button>)}
                        <ClientCharts clientId={detail.client_id} clientName={detail.client_name} mode="visit" bookingId={detail.id} existing={visitCharts} onChanged={() => loadVisitCharts(detail)} openId={openChartId} onClosed={() => { setOpenChartId(null); loadVisitCharts(detail); }} />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1.5">
                    <textarea value={tnText} onChange={(e) => setTnText(e.target.value)} placeholder="Quick note for this visit — settings, skin response, advice given… (locked once saved; no chart needed)" className="flex-1 rounded-lg border border-input bg-background p-3 text-sm min-h-[56px]" data-testid="tn-input" />
                    <Button size="sm" className="rounded-lg self-end" onClick={addTreatmentNote} disabled={tnSaving || !tnText.trim()} data-testid="tn-add">{tnSaving ? "Saving…" : "Add note"}</Button>
                  </div>
                  {tnotes.length > 0 && (
                    <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="tn-list">
                      {tnotes.map((n) => (
                        <div key={n.id} className="rounded-lg border border-border/70 bg-muted/30 p-2.5 text-sm" data-testid={`tn-${n.id}`}>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground mb-1">
                            <span className="tabular-nums">{new Date(n.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                            <span>{n.author_name}{n.booking_id !== detail.id && n.service_name ? ` · ${n.service_name}` : ""}</span>
                          </div>
                          <div className="whitespace-pre-wrap">{n.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {detail.notes && <div className="text-[11px] text-muted-foreground mt-2 italic">Booking note: {detail.notes}</div>}
                </div>
              </div>
            )}
            {!editing && (
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {detail.series_id && (
                  <div className="mr-auto w-full sm:w-auto space-y-2" data-testid="series-actions">
                    <div className="text-xs font-medium text-muted-foreground">Recurring series</div>
                    <div className="flex gap-2 flex-wrap items-center">
                      {seriesMove.open ? (
                        <div className="flex gap-2 items-end flex-wrap" data-testid="series-move-form">
                          <div><label className="text-[10px] text-muted-foreground">New date</label><Input type="date" value={seriesMove.date} onChange={(e) => setSeriesMove((s) => ({ ...s, date: e.target.value }))} className="h-9 rounded-lg" data-testid="series-move-date" /></div>
                          <div><label className="text-[10px] text-muted-foreground">Time</label><Input type="time" value={seriesMove.time} onChange={(e) => setSeriesMove((s) => ({ ...s, time: e.target.value }))} className="h-9 rounded-lg w-28" data-testid="series-move-time" /></div>
                          <Button size="sm" className="rounded-lg" onClick={moveSeries} data-testid="series-move-apply">Move all</Button>
                          <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setSeriesMove({ open: false, date: "", time: "09:00" })}>Cancel</Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setSeriesMove({ open: true, date: (detail.start || "").slice(0, 10), time: (detail.start || "").slice(11, 16) || "09:00" })} data-testid="series-move-open"><CalendarPlus className="h-4 w-4 mr-1.5" />Move series</Button>
                      )}
                      <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => seriesAction("delete", "all")} data-testid="series-delete-all"><Trash2 className="h-4 w-4 mr-1" />Delete series</Button>
                    </div>
                  </div>
                )}
                {(detail.paid || detail.status === "checked_out") ? (
                  <p className="text-xs text-muted-foreground w-full" data-testid="booking-paid-no-cancel">Paid — this booking went ahead, so it can't be cancelled. To give money back, use <b>Refund</b> in the payment panel or the client's Invoices.</p>
                ) : (
                  <Button variant="outline" className="rounded-lg text-destructive" onClick={() => remove(detail)} data-testid="booking-cancel-detail"><Trash2 className="h-4 w-4 mr-1.5" />Cancel {detail.group_id && bookings.filter((x) => x.group_id === detail.group_id && x.status !== "cancelled" && x.status !== "checked_out").length > 1 ? "this service" : "booking"}</Button>
                )}
                {!(detail.paid || detail.status === "checked_out") && detail.group_id && bookings.filter((x) => x.group_id === detail.group_id && x.status !== "cancelled" && x.status !== "checked_out").length > 1 && (
                  <Button variant="outline" className="rounded-lg text-destructive" onClick={() => cancelVisit(detail)} data-testid="booking-cancel-visit"><Layers className="h-4 w-4 mr-1.5" />Cancel whole visit</Button>
                )}
              </DialogFooter>
            )}
          </>}
        </DialogContent>
      </Dialog>

      {/* Checkout dialog */}
      {checkout && (
        <CheckoutDialog booking={checkout} cur={cur} isAdmin={isAdmin}
          visitServices={checkout.group_id ? bookings.filter((x) => x.group_id === checkout.group_id && x.status !== "cancelled" && x.status !== "checked_out").sort((a, b) => (a.start < b.start ? -1 : 1)) : []}
          onClose={() => setCheckout(null)}
          onDone={(b) => { setCheckout(null); load(); if (!b?.skip_rebook_prompt) setRebookFor(b); }} />
      )}

      <QuickSaleDialog open={quickSaleOpen} onOpenChange={setQuickSaleOpen} cur={cur} onDone={load} />

      {/* Post-checkout rebook prompt */}
      <Dialog open={!!rebookFor} onOpenChange={(v) => !v && setRebookFor(null)}>
        <DialogContent>
          {rebookFor && <>
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-primary" />Rebook {rebookFor.client_name}?</DialogTitle><DialogDescription>Same client, service & staff — jump ahead and pick the slot.</DialogDescription></DialogHeader>
            <div className="flex flex-wrap gap-2 py-2">
              {REBOOK_WEEKS.map((w) => (
                <Button key={w} variant="outline" className="rounded-lg" onClick={() => rebookNav(rebookFor, w)} data-testid={`rebook-${w}`}>+{w} weeks</Button>
              ))}
            </div>
            <DialogFooter><Button variant="ghost" className="rounded-lg" onClick={() => setRebookFor(null)} data-testid="rebook-skip">Skip for now</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {deposit && <DepositDialog booking={deposit} onClose={(changed) => { setDeposit(null); if (changed) load(); }} />}
      <CancelDialog open={!!cancelReq} onOpenChange={(v) => !v && setCancelReq(null)} title={cancelReq?.title} description={cancelReq?.description} confirmLabel={cancelReq?.confirmLabel} onConfirm={runCancel} />
    </div>
  );
}
